import React from 'react';

import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    booleanValue,
    formatRecordInline,
    isRecord,
    JsonValue,
    numberValue,
    renderUntrustedString,
    stringsFromUnknown,
    stringValue,
    stripUntrustedMarkers,
    toRecord,
    UNTRUSTED_END_MARKER,
    UNTRUSTED_START_MARKER,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

type CodebaseWikiMatch = {
    readonly path: string;
    readonly sourceUrl: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly language: string | null;
    readonly score: number | null;
    readonly reasons: ReadonlyArray<string>;
    readonly symbols: ReadonlyArray<string>;
    readonly imports: ReadonlyArray<string>;
    readonly headings: ReadonlyArray<string>;
    readonly snippet: string | null;
};

type CodebaseWikiKnowledgeMatch = {
    readonly path: string;
    readonly documentType: string;
    readonly title: string;
    readonly sourceUrl: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly score: number | null;
    readonly reasons: ReadonlyArray<string>;
    readonly snippet: string | null;
};

type CodebaseWikiReferenceMatch = {
    readonly path: string;
    readonly sourceUrl: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly language: string | null;
    readonly score: number | null;
    readonly reasons: ReadonlyArray<string>;
    readonly referenceKind: string;
    readonly symbolName: string;
    readonly lineText: string | null;
    readonly readCommand: string | null;
};

type CodebaseWikiOutlineSymbol = {
    readonly name: string;
    readonly kind: string;
    readonly parentName: string | null;
    readonly signature: string | null;
    readonly scopeDepth: number | null;
    readonly childCount: number | null;
    readonly decorators: ReadonlyArray<string>;
    readonly tags: ReadonlyArray<string>;
    readonly sourceUrl: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
};

type CodebaseWikiOutline = {
    readonly path: string;
    readonly language: string;
    readonly lineCount: number | null;
    readonly score: number | null;
    readonly reasons: ReadonlyArray<string>;
    readonly roles: ReadonlyArray<string>;
    readonly imports: ReadonlyArray<string>;
    readonly headings: ReadonlyArray<string>;
    readonly symbols: ReadonlyArray<CodebaseWikiOutlineSymbol>;
    readonly sourceUrl: string | null;
};

type CodebaseWikiPage = {
    readonly title: string;
    readonly purpose: string;
    readonly parent: string | null;
    readonly sourcePaths: ReadonlyArray<string>;
    readonly pageNotes: ReadonlyArray<string>;
};

type CodebaseWikiPageContent = CodebaseWikiPage & {
    readonly content: string;
    readonly diagramMermaid: string | null;
    readonly diagramEdges: ReadonlyArray<CodebaseWikiDiagramEdge>;
};

type CodebaseWikiDiagramEdge = {
    readonly from: string;
    readonly to: string;
    readonly kind: string | null;
};

type CodebaseWikiAnswer = {
    readonly question: string;
    readonly answer: string;
    readonly citations: ReadonlyArray<string>;
};

type CodebaseWikiContextReference = {
    readonly path: string;
    readonly kind: string;
    readonly reason: string;
    readonly sourceUrl: string | null;
};

type CodebaseWikiContextSubsystem = {
    readonly name: string;
    readonly description: string;
    readonly confidence: string;
    readonly reasons: ReadonlyArray<string>;
    readonly sourcePaths: ReadonlyArray<string>;
};

type CodebaseWikiRuntimeSignal = {
    readonly name: string;
    readonly purpose: string;
    readonly tools: ReadonlyArray<string>;
    readonly sourcePaths: ReadonlyArray<string>;
};

type CodebaseWikiRuntimeAnchor = {
    readonly kind: string;
    readonly identifier: string;
    readonly label: string;
    readonly recommendedTool: string;
    readonly lookupHint: string;
    readonly reason: string;
    readonly url: string | null;
    readonly sourcePaths: ReadonlyArray<string>;
};

type CodebaseWikiContextRelationship = {
    readonly sourcePath: string;
    readonly targetPath: string;
    readonly relationship: string;
    readonly reason: string;
    readonly sourceUrl: string | null;
    readonly targetSourceUrl: string | null;
};

type CodebaseWikiContextPack = {
    readonly task: string;
    readonly summary: string;
    readonly confidence: string;
    readonly primarySubsystem: CodebaseWikiContextSubsystem | null;
    readonly relatedSubsystems: ReadonlyArray<CodebaseWikiContextSubsystem>;
    readonly wikiPages: ReadonlyArray<CodebaseWikiPage>;
    readonly sourceReferences: ReadonlyArray<CodebaseWikiContextReference>;
    readonly testReferences: ReadonlyArray<CodebaseWikiContextReference>;
    readonly documentationReferences: ReadonlyArray<CodebaseWikiContextReference>;
    readonly runtimeSignals: ReadonlyArray<CodebaseWikiRuntimeSignal>;
    readonly runtimeAnchors: ReadonlyArray<CodebaseWikiRuntimeAnchor>;
    readonly codeRelationships: ReadonlyArray<CodebaseWikiContextRelationship>;
    readonly playbookSteps: ReadonlyArray<string>;
    readonly openQuestions: ReadonlyArray<string>;
    readonly handoffPrompt: string;
};

type CodebaseWikiLocalizationSymbol = {
    readonly name: string;
    readonly kind: string;
    readonly path: string;
    readonly reasons: ReadonlyArray<string>;
};

type CodebaseWikiLocalizationRelationship = {
    readonly sourcePath: string;
    readonly targetPath: string | null;
    readonly importName: string;
    readonly kind: string;
    readonly relationship: string | null;
    readonly reasons: ReadonlyArray<string>;
};

type CodebaseWikiSourceLine = {
    readonly lineNumber: number;
    readonly text: string;
};

type CodebaseWikiSourceRange = {
    readonly path: string;
    readonly lineStart: number;
    readonly lineEnd: number;
    readonly sourceUrl: string | null;
    readonly lines: ReadonlyArray<CodebaseWikiSourceLine>;
};

type CodebaseWikiLocalizationCandidate = {
    readonly path: string;
    readonly confidence: string;
    readonly score: number | null;
    readonly reasons: ReadonlyArray<string>;
    readonly symbols: ReadonlyArray<CodebaseWikiLocalizationSymbol>;
    readonly relationships: ReadonlyArray<CodebaseWikiLocalizationRelationship>;
    readonly sourceRanges: ReadonlyArray<CodebaseWikiSourceRange>;
    readonly testReferences: ReadonlyArray<CodebaseWikiContextReference>;
    readonly sourceBackedFindings: ReadonlyArray<string>;
    readonly hypotheses: ReadonlyArray<string>;
};

type CodebaseWikiLocalizationTrailStep = {
    readonly operation: string;
    readonly query: string;
    readonly path: string | null;
    readonly resultCount: number | null;
    readonly inspectedPaths: ReadonlyArray<string>;
    readonly notes: ReadonlyArray<string>;
};

type CodebaseWikiLocalizationRuledOut = {
    readonly path: string | null;
    readonly reason: string | null;
    readonly evidence: ReadonlyArray<string>;
};

type CodebaseWikiLocalization = {
    readonly summary: string;
    readonly confidence: string;
    readonly evidenceReferences: ReadonlyArray<string>;
    readonly candidateLocations: ReadonlyArray<CodebaseWikiLocalizationCandidate>;
    readonly lowConfidenceCandidates: ReadonlyArray<CodebaseWikiLocalizationCandidate>;
    readonly testReferences: ReadonlyArray<CodebaseWikiContextReference>;
    readonly searchTrail: ReadonlyArray<CodebaseWikiLocalizationTrailStep>;
    readonly ruledOut: ReadonlyArray<CodebaseWikiLocalizationRuledOut>;
    readonly sourceBackedFindings: ReadonlyArray<string>;
    readonly hypotheses: ReadonlyArray<string>;
};

type CodebaseWikiRuntimeEvidenceLookup = {
    readonly reference: string;
    readonly kind: string;
    readonly status: string;
    readonly identifier: string | null;
    readonly environment: string | null;
    readonly sourceTool: string | null;
    readonly extractedAnchorCount: number | null;
    readonly notes: ReadonlyArray<string>;
};

type CodebaseWikiRuntimeEvidenceAnchor = {
    readonly kind: string;
    readonly label: string;
    readonly value: string;
    readonly sourceReference: string;
    readonly sourceBacked: boolean;
    readonly confidence: string;
    readonly citation: string | null;
};

type CodebaseWikiRuntimeEvidenceHandoff = {
    readonly issue: string;
    readonly evidenceReferences: ReadonlyArray<string>;
    readonly lookups: ReadonlyArray<CodebaseWikiRuntimeEvidenceLookup>;
    readonly anchors: ReadonlyArray<CodebaseWikiRuntimeEvidenceAnchor>;
    readonly unsupportedReferences: ReadonlyArray<string>;
    readonly sourcePathCandidates: ReadonlyArray<string>;
    readonly localizationQuery: string;
    readonly localization: CodebaseWikiLocalization;
};

type CodebaseWikiContextPage = {
    readonly pageId: string;
    readonly path: string;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly lineCount: number | null;
    readonly sourceUrl: string | null;
    readonly reason: string;
    readonly readCommand: string;
};

type CodebaseWikiInvestigation = {
    readonly question: string;
    readonly mode: string;
    readonly evidenceReferences: ReadonlyArray<string>;
    readonly summary: string;
    readonly confidence: string;
    readonly mapRoute: CodebaseWikiInvestigationMapRoute | null;
    readonly queryPlan: CodebaseWikiInvestigationQueryPlan | null;
    readonly citations: ReadonlyArray<CodebaseWikiInvestigationCitation>;
    readonly contextPages: ReadonlyArray<CodebaseWikiContextPage>;
    readonly sourceReadBudget: CodebaseWikiSourceReadBudget | null;
    readonly qualitySignals: CodebaseWikiInvestigationQualitySignals | null;
    readonly llmRerank: CodebaseWikiInvestigationRerank | null;
    readonly investigationPlan: ReadonlyArray<string>;
    readonly candidateLocations: ReadonlyArray<CodebaseWikiLocalizationCandidate>;
    readonly lowConfidenceCandidates: ReadonlyArray<CodebaseWikiLocalizationCandidate>;
    readonly keyFiles: ReadonlyArray<CodebaseWikiContextReference>;
    readonly keySymbols: ReadonlyArray<CodebaseWikiLocalizationSymbol>;
    readonly relatedTests: ReadonlyArray<CodebaseWikiContextReference>;
    readonly knowledgeDocuments: ReadonlyArray<CodebaseWikiKnowledgeMatch>;
    readonly keyOutlines: ReadonlyArray<CodebaseWikiOutline>;
    readonly evidencePath: ReadonlyArray<CodebaseWikiEvidencePathStep>;
    readonly sourceBackedFindings: ReadonlyArray<string>;
    readonly knowledgeOnlyLeads: ReadonlyArray<string>;
    readonly hypotheses: ReadonlyArray<string>;
    readonly ruledOut: ReadonlyArray<CodebaseWikiLocalizationRuledOut>;
    readonly searchTrail: ReadonlyArray<CodebaseWikiLocalizationTrailStep>;
    readonly missingContext: ReadonlyArray<string>;
    readonly suggestedNextReads: ReadonlyArray<string>;
};

type CodebaseWikiInvestigationRerank = {
    readonly status: string;
    readonly rerankModel: string;
    readonly summary: string;
    readonly confidence: string;
    readonly likelyFiles: ReadonlyArray<CodebaseWikiInvestigationRerankFile>;
    readonly likelySymbols: ReadonlyArray<string>;
    readonly testsToRead: ReadonlyArray<string>;
    readonly ruledOut: ReadonlyArray<string>;
    readonly suggestedNextReads: ReadonlyArray<string>;
    readonly citationIndex: ReadonlyArray<CodebaseWikiEvidenceReviewCitation>;
    readonly warnings: ReadonlyArray<string>;
    readonly llmUsage: Record<string, number>;
};

type CodebaseWikiInvestigationRerankFile = {
    readonly path: string;
    readonly reason: string;
    readonly confidence: string;
    readonly evidenceCitationIds: ReadonlyArray<string>;
    readonly symbols: ReadonlyArray<string>;
};

type CodebaseWikiEvidenceReview = {
    readonly question: string;
    readonly reviewModel: string;
    readonly summary: string;
    readonly verdict: string;
    readonly confidence: string;
    readonly findings: ReadonlyArray<CodebaseWikiEvidenceReviewFinding>;
    readonly missingContext: ReadonlyArray<string>;
    readonly suggestedNextReads: ReadonlyArray<string>;
    readonly citationIndex: ReadonlyArray<CodebaseWikiEvidenceReviewCitation>;
    readonly warnings: ReadonlyArray<string>;
    readonly llmUsage: Record<string, number>;
};

type CodebaseWikiEvidenceReviewFinding = {
    readonly severity: string;
    readonly category: string;
    readonly title: string;
    readonly claim: string;
    readonly evidenceCitationIds: ReadonlyArray<string>;
    readonly affectedPaths: ReadonlyArray<string>;
    readonly confidence: string;
    readonly nextStep: string | null;
};

type CodebaseWikiEvidenceReviewCitation = {
    readonly citationId: string;
    readonly evidenceType: string;
    readonly label: string;
    readonly reason: string;
    readonly confidence: string | null;
    readonly path: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly symbol: string | null;
    readonly sourceUrl: string | null;
};

type CodebaseWikiInvestigationMapMatch = {
    readonly label: string;
    readonly nodeType: string;
    readonly path: string | null;
    readonly reasons: ReadonlyArray<string>;
};

type CodebaseWikiInvestigationMapNextRead = {
    readonly tool: string;
    readonly reason: string;
    readonly path: string | null;
    readonly query: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
};

type CodebaseWikiInvestigationMapRouteHop = {
    readonly label: string;
    readonly nodeType: string;
    readonly reason: string;
    readonly path: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly sourceUrl: string | null;
};

type CodebaseWikiInvestigationMapRoutePath = {
    readonly label: string;
    readonly confidence: string;
    readonly score: number | null;
    readonly hops: ReadonlyArray<CodebaseWikiInvestigationMapRouteHop>;
};

type CodebaseWikiInvestigationMapSummary = {
    readonly label: string;
    readonly summary: string;
    readonly sourcePaths: ReadonlyArray<string>;
    readonly entryFiles: ReadonlyArray<string>;
    readonly tests: ReadonlyArray<string>;
    readonly docs: ReadonlyArray<string>;
    readonly symbols: ReadonlyArray<string>;
};

type CodebaseWikiIndexValidation = {
    readonly status: string;
    readonly warningCount: number | null;
    readonly nodeCount: number | null;
    readonly edgeCount: number | null;
    readonly summaryCount: number | null;
    readonly warnings: ReadonlyArray<string>;
};

type CodebaseWikiInvestigationMapRoute = {
    readonly confidence: string;
    readonly likelySubsystems: ReadonlyArray<CodebaseWikiInvestigationMapMatch>;
    readonly entryFiles: ReadonlyArray<CodebaseWikiInvestigationMapMatch>;
    readonly symbols: ReadonlyArray<CodebaseWikiInvestigationMapMatch>;
    readonly tests: ReadonlyArray<CodebaseWikiInvestigationMapMatch>;
    readonly docs: ReadonlyArray<CodebaseWikiInvestigationMapMatch>;
    readonly suggestedNextReads: ReadonlyArray<CodebaseWikiInvestigationMapNextRead>;
    readonly routePaths: ReadonlyArray<CodebaseWikiInvestigationMapRoutePath>;
    readonly structuralSummaries: ReadonlyArray<CodebaseWikiInvestigationMapSummary>;
    readonly indexValidation: CodebaseWikiIndexValidation | null;
    readonly warnings: ReadonlyArray<string>;
};

type CodebaseWikiInvestigationQueryPlan = {
    readonly strategy: string;
    readonly traversalPolicy: ReadonlyArray<string>;
    readonly primaryTools: ReadonlyArray<string>;
    readonly fallbackTools: ReadonlyArray<string>;
};

type CodebaseWikiInvestigationCitation = {
    readonly evidenceType: string;
    readonly label: string;
    readonly reason: string;
    readonly confidence: string | null;
    readonly path: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly symbol: string | null;
    readonly sourceUrl: string | null;
};

type CodebaseWikiEvidencePathStep = {
    readonly stage: string;
    readonly tool: string;
    readonly label: string;
    readonly reason: string;
    readonly confidence: string | null;
    readonly path: string | null;
    readonly symbol: string | null;
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
    readonly sourceUrl: string | null;
};

type CodebaseWikiSourceReadBudget = {
    readonly maxNextReads: number | null;
    readonly candidateCount: number | null;
    readonly outlineCount: number | null;
    readonly sourceRangeCount: number | null;
    readonly mapReadCount: number | null;
    readonly inlineSourceLineCount: number | null;
    readonly maxInlineSourceLines: number | null;
    readonly contextPageCount: number | null;
    readonly dedupedNextReadCount: number | null;
    readonly capped: boolean;
    readonly warnings: ReadonlyArray<string>;
};

type CodebaseWikiInvestigationQualitySignals = {
    readonly hasRoutePaths: boolean;
    readonly evidencePathCount: number | null;
    readonly outlineCount: number | null;
    readonly citationCount: number | null;
    readonly sourceBackedCandidateCount: number | null;
    readonly indexValidationStatus: string | null;
    readonly mapWarningCount: number | null;
    readonly missingContextCount: number | null;
    readonly contextPageCount: number | null;
    readonly runtimeAnchorCount: number | null;
    readonly runtimeSourceReferenceCount: number | null;
};

export function CodebaseWikiArtifact({ artifact }: AdminArtifactRendererProps) {
    const payload = toRecord(artifact.payload);
    const atlasSummary = toRecord(payload['atlas_summary']);
    const wikiPages = getCodebaseWikiPages(artifact.payload);
    const wikiPage = getCodebaseWikiPageContent(artifact.payload);
    const answer = getCodebaseWikiAnswer(artifact.payload);
    const contextPack = getCodebaseWikiContextPack(artifact.payload);
    const runtimeHandoff = getCodebaseWikiRuntimeHandoff(artifact.payload);
    const investigation = getCodebaseWikiInvestigation(artifact.payload);
    const evidenceReview = getCodebaseWikiEvidenceReview(artifact.payload);
    const localization =
        investigation === null
            ? (getCodebaseWikiLocalization(artifact.payload) ??
              runtimeHandoff?.localization ??
              null)
            : null;
    const matches = getCodebaseWikiMatches(artifact.payload);
    const references = getCodebaseWikiReferences(artifact.payload);
    const knowledgeMatches = getCodebaseWikiKnowledgeMatches(artifact.payload);
    const outlines = getCodebaseWikiOutlines(artifact.payload);
    const fileCount = numberValue(atlasSummary['file_count']);
    const languageCounts = toRecord(atlasSummary['language_counts']);
    const revision = stringValue(atlasSummary['revision']);
    const branch = stringValue(atlasSummary['branch']);
    const indexStatus = stringValue(atlasSummary['index_status']);
    const wikiConfig = toRecord(atlasSummary['wiki_config']);
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div className="grid gap-2 text-xs md:grid-cols-6">
                <CodebaseWikiMetric
                    label="Indexed files"
                    value={fileCount === null ? 'Unknown' : String(fileCount)}
                />
                <CodebaseWikiMetric
                    label="Revision"
                    value={formatCodebaseRevision({ branch, revision })}
                />
                <CodebaseWikiMetric
                    label="Source paths"
                    value={stringsFromUnknown(
                        atlasSummary['source_paths'],
                    ).join(', ')}
                />
                <CodebaseWikiMetric
                    label="Languages"
                    value={formatRecordInline(languageCounts)}
                />
                <CodebaseWikiMetric
                    label="Wiki config"
                    value={formatCodebaseWikiConfig(wikiConfig)}
                />
                <CodebaseWikiMetric
                    label="Index"
                    value={formatCodebaseIndexStatus(indexStatus)}
                />
            </div>
            {contextPack ? (
                <CodebaseWikiContextPackView contextPack={contextPack} />
            ) : null}
            {investigation ? (
                <CodebaseWikiInvestigationView investigation={investigation} />
            ) : null}
            {evidenceReview ? (
                <CodebaseWikiEvidenceReviewView review={evidenceReview} />
            ) : null}
            {runtimeHandoff ? (
                <CodebaseWikiRuntimeHandoffView handoff={runtimeHandoff} />
            ) : null}
            {localization ? (
                <CodebaseWikiLocalizationView localization={localization} />
            ) : null}
            {wikiPages.length > 0 ? (
                <section className="space-y-2">
                    <h5 className="text-foreground text-xs font-medium">
                        Wiki pages
                    </h5>
                    <ol className="grid gap-2 md:grid-cols-2">
                        {wikiPages.map((page, index) => (
                            <CodebaseWikiPageView
                                key={`${page.title}-${index}`}
                                page={page}
                            />
                        ))}
                    </ol>
                </section>
            ) : null}
            {wikiPage ? <CodebaseWikiPageContentView page={wikiPage} /> : null}
            {answer ? <CodebaseWikiAnswerView answer={answer} /> : null}
            {matches.length > 0 ? (
                <ol className="space-y-2">
                    {matches.map((match, index) => (
                        <CodebaseWikiMatchView
                            key={codebaseWikiMatchKey(match, index)}
                            match={match}
                        />
                    ))}
                </ol>
            ) : null}
            {references.length > 0 ? (
                <section className="space-y-2">
                    <h5 className="text-foreground text-xs font-medium">
                        Reference matches
                    </h5>
                    <ol className="space-y-2">
                        {references.map((reference, index) => (
                            <CodebaseWikiReferenceMatchView
                                key={codebaseWikiReferenceMatchKey(
                                    reference,
                                    index,
                                )}
                                reference={reference}
                            />
                        ))}
                    </ol>
                </section>
            ) : null}
            {outlines.length > 0 ? (
                <section className="space-y-2">
                    <h5 className="text-foreground text-xs font-medium">
                        File outlines
                    </h5>
                    <ol className="space-y-2">
                        {outlines.map((outline, index) => (
                            <CodebaseWikiOutlineView
                                key={codebaseWikiOutlineKey(outline, index)}
                                outline={outline}
                            />
                        ))}
                    </ol>
                </section>
            ) : null}
            {knowledgeMatches.length > 0 ? (
                <section className="space-y-2">
                    <h5 className="text-foreground text-xs font-medium">
                        Knowledge matches
                    </h5>
                    <ol className="space-y-2">
                        {knowledgeMatches.map((match, index) => (
                            <CodebaseWikiKnowledgeMatchView
                                key={codebaseWikiKnowledgeMatchKey(
                                    match,
                                    index,
                                )}
                                match={match}
                            />
                        ))}
                    </ol>
                </section>
            ) : null}
            {matches.length === 0 &&
            references.length === 0 &&
            outlines.length === 0 &&
            knowledgeMatches.length === 0 &&
            !investigation &&
            !evidenceReview ? (
                <p className="text-muted-foreground text-xs">
                    No source or knowledge matches were included in this
                    artifact.
                </p>
            ) : null}
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <JsonValue value={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}

function CodebaseWikiPageView({ page }: { readonly page: CodebaseWikiPage }) {
    return (
        <li className="border-border/60 space-y-1 border-l pl-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground min-w-0 text-xs font-medium break-words">
                    {renderUntrustedString(page.title)}
                </span>
                {page.parent ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        parent {renderUntrustedString(page.parent)}
                    </span>
                ) : null}
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed">
                {renderUntrustedString(page.purpose)}
            </p>
            <CodebaseWikiList label="Notes" values={page.pageNotes} />
            <CodebaseWikiList label="Sources" values={page.sourcePaths} />
        </li>
    );
}

function CodebaseWikiPageContentView({
    page,
}: {
    readonly page: CodebaseWikiPageContent;
}) {
    return (
        <section className="border-border/60 space-y-2 border-t pt-2.5">
            <h5 className="text-foreground text-xs font-medium">
                {renderUntrustedString(page.title)}
            </h5>
            <p className="text-muted-foreground text-xs leading-relaxed">
                {renderUntrustedString(page.purpose)}
            </p>
            <CodebaseWikiList label="Notes" values={page.pageNotes} />
            <pre className="bg-muted/40 text-muted-foreground overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                {renderUntrustedString(page.content)}
            </pre>
            {page.diagramMermaid ? (
                <CodebaseWikiDiagramView
                    edges={page.diagramEdges}
                    mermaid={page.diagramMermaid}
                />
            ) : null}
        </section>
    );
}

function CodebaseWikiDiagramView({
    edges,
    mermaid,
}: {
    readonly edges: ReadonlyArray<CodebaseWikiDiagramEdge>;
    readonly mermaid: string;
}) {
    const visibleEdges =
        edges.length > 0 ? edges : getCodebaseWikiDiagramEdges(mermaid);
    const nodes = Array.from(
        new Set(visibleEdges.flatMap((edge) => [edge.from, edge.to])),
    );
    return (
        <div className="space-y-1.5">
            {visibleEdges.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Rendered diagram
                    </h6>
                    <div
                        aria-label="Codebase wiki source graph"
                        className="bg-muted/20 border-border/60 grid gap-2 rounded border p-2 text-[11px] md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]"
                        role="img"
                    >
                        <div className="space-y-1">
                            <div className="text-muted-foreground/70">
                                Nodes
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {nodes.map((node) => (
                                    <span
                                        key={node}
                                        className="bg-background text-foreground border-border/60 max-w-full rounded border px-1.5 py-0.5 font-mono break-all"
                                    >
                                        {renderUntrustedString(node)}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="text-muted-foreground/70">
                                Edges
                            </div>
                            <ol className="space-y-1">
                                {visibleEdges.map((edge, index) => (
                                    <li
                                        key={`${edge.from}-${edge.to}-${index}`}
                                        className="text-muted-foreground font-mono break-all"
                                    >
                                        {renderUntrustedString(edge.from)}
                                        <span className="text-muted-foreground/40">
                                            {' -> '}
                                        </span>
                                        {renderUntrustedString(edge.to)}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>
            ) : null}
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Mermaid diagram
                </summary>
                <pre className="bg-muted/40 text-muted-foreground mt-1.5 overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                    {renderUntrustedString(mermaid)}
                </pre>
            </details>
        </div>
    );
}

function CodebaseWikiAnswerView({
    answer,
}: {
    readonly answer: CodebaseWikiAnswer;
}) {
    return (
        <section className="border-border/60 space-y-1.5 border-t pt-2.5">
            <h5 className="text-foreground text-xs font-medium">Answer</h5>
            <p className="text-muted-foreground/70 text-[11px]">
                {renderUntrustedString(answer.question)}
            </p>
            <p className="text-muted-foreground text-xs leading-relaxed">
                {renderUntrustedString(answer.answer)}
            </p>
            <CodebaseWikiList label="Citations" values={answer.citations} />
        </section>
    );
}

function CodebaseWikiContextPackView({
    contextPack,
}: {
    readonly contextPack: CodebaseWikiContextPack;
}) {
    return (
        <section className="border-border/60 space-y-2 border-t pt-2.5">
            <div className="space-y-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h5 className="text-foreground text-xs font-medium">
                        Context pack
                    </h5>
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(contextPack.confidence)}
                    </span>
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {renderUntrustedString(contextPack.summary)}
                </p>
            </div>
            {contextPack.primarySubsystem ? (
                <div className="border-border/60 space-y-1 border-l pl-3">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-foreground text-xs font-medium">
                            {renderUntrustedString(
                                contextPack.primarySubsystem.name,
                            )}
                        </span>
                        <span className="text-muted-foreground/70 text-[11px]">
                            {renderUntrustedString(
                                contextPack.primarySubsystem.confidence,
                            )}
                        </span>
                    </div>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                        {renderUntrustedString(
                            contextPack.primarySubsystem.description,
                        )}
                    </p>
                    <CodebaseWikiList
                        label="Reasons"
                        values={contextPack.primarySubsystem.reasons}
                    />
                    <CodebaseWikiList
                        label="Sources"
                        values={contextPack.primarySubsystem.sourcePaths}
                    />
                </div>
            ) : null}
            {contextPack.relatedSubsystems.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Related subsystems
                    </h6>
                    <ol className="space-y-1.5">
                        {contextPack.relatedSubsystems.map(
                            (subsystem, index) => (
                                <CodebaseWikiSubsystemView
                                    key={`${subsystem.name}-${index}`}
                                    subsystem={subsystem}
                                />
                            ),
                        )}
                    </ol>
                </div>
            ) : null}
            {contextPack.wikiPages.length > 0 ? (
                <section className="space-y-2">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Ranked wiki pages
                    </h6>
                    <ol className="space-y-2">
                        {contextPack.wikiPages.map((page, index) => (
                            <CodebaseWikiPageView
                                key={`${page.title}-${index}`}
                                page={page}
                            />
                        ))}
                    </ol>
                </section>
            ) : null}
            <CodebaseWikiReferenceList
                label="Source references"
                references={contextPack.sourceReferences}
            />
            <CodebaseWikiReferenceList
                label="Likely tests"
                references={contextPack.testReferences}
            />
            <CodebaseWikiReferenceList
                label="Docs"
                references={contextPack.documentationReferences}
            />
            {contextPack.runtimeSignals.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Runtime evidence
                    </h6>
                    <ol className="space-y-1.5">
                        {contextPack.runtimeSignals.map((signal) => (
                            <li
                                key={signal.name}
                                className="border-border/60 space-y-1 border-l pl-3"
                            >
                                <span className="text-foreground text-xs font-medium">
                                    {renderUntrustedString(signal.name)}
                                </span>
                                <p className="text-muted-foreground text-xs leading-relaxed">
                                    {renderUntrustedString(signal.purpose)}
                                </p>
                                <CodebaseWikiList
                                    label="Tools"
                                    values={signal.tools}
                                />
                                <CodebaseWikiList
                                    label="Sources"
                                    values={signal.sourcePaths}
                                />
                            </li>
                        ))}
                    </ol>
                </div>
            ) : null}
            {contextPack.runtimeAnchors.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Runtime anchors
                    </h6>
                    <ol className="space-y-1.5">
                        {contextPack.runtimeAnchors.map((anchor) => (
                            <li
                                key={`${anchor.kind}-${anchor.identifier}`}
                                className="border-border/60 space-y-1 border-l pl-3"
                            >
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                    <span className="text-foreground text-xs font-medium">
                                        {renderUntrustedString(anchor.label)}
                                    </span>
                                    <span className="text-muted-foreground/70 font-mono text-[11px] break-all">
                                        {renderUntrustedString(
                                            anchor.identifier,
                                        )}
                                    </span>
                                </div>
                                <p className="text-muted-foreground text-xs leading-relaxed">
                                    {renderUntrustedString(anchor.reason)}
                                </p>
                                <CodebaseWikiList
                                    label="Lookup"
                                    values={[
                                        anchor.recommendedTool,
                                        anchor.lookupHint,
                                    ]}
                                />
                                {anchor.url ? (
                                    <CodebaseWikiList
                                        label="URL"
                                        values={[anchor.url]}
                                    />
                                ) : null}
                                <CodebaseWikiList
                                    label="Sources"
                                    values={anchor.sourcePaths}
                                />
                            </li>
                        ))}
                    </ol>
                </div>
            ) : null}
            {contextPack.codeRelationships.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Code relationships
                    </h6>
                    <ol className="space-y-1">
                        {contextPack.codeRelationships.map((relationship) => (
                            <li
                                key={`${relationship.relationship}-${relationship.sourcePath}-${relationship.targetPath}`}
                                className="text-muted-foreground text-[11px]"
                            >
                                <span className="text-foreground font-mono break-all">
                                    {renderUntrustedString(
                                        relationship.sourcePath,
                                    )}
                                </span>{' '}
                                <span>
                                    {renderUntrustedString(
                                        relationship.relationship,
                                    )}
                                </span>{' '}
                                <span className="text-foreground font-mono break-all">
                                    {renderUntrustedString(
                                        relationship.targetPath,
                                    )}
                                </span>
                                <span className="text-muted-foreground/60">
                                    {' · '}
                                </span>
                                <span>
                                    {renderUntrustedString(relationship.reason)}
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
            ) : null}
            <CodebaseWikiList
                label="Playbook"
                values={contextPack.playbookSteps}
            />
            <CodebaseWikiList
                label="Open questions"
                values={contextPack.openQuestions}
            />
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Handoff prompt
                </summary>
                <pre className="bg-muted/40 text-muted-foreground mt-1.5 overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                    {renderUntrustedString(contextPack.handoffPrompt)}
                </pre>
            </details>
        </section>
    );
}

function CodebaseWikiInvestigationView({
    investigation,
}: {
    readonly investigation: CodebaseWikiInvestigation;
}) {
    return (
        <section className="border-border/60 space-y-2 border-t pt-2.5">
            <div className="space-y-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h5 className="text-foreground text-xs font-medium">
                        Codebase investigation
                    </h5>
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(investigation.mode)}
                        {' · '}
                        {renderUntrustedString(investigation.confidence)}
                    </span>
                </div>
                <p className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(investigation.question)}
                </p>
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {renderUntrustedString(investigation.summary)}
                </p>
                <CodebaseWikiList
                    label="Evidence references"
                    values={investigation.evidenceReferences}
                />
            </div>
            <CodebaseWikiList
                label="Plan"
                values={investigation.investigationPlan}
            />
            <CodebaseWikiInvestigationQueryPlanView
                queryPlan={investigation.queryPlan}
            />
            <CodebaseWikiSourceReadBudgetView
                budget={investigation.sourceReadBudget}
            />
            <CodebaseWikiInvestigationQualitySignalsView
                qualitySignals={investigation.qualitySignals}
            />
            <CodebaseWikiInvestigationRerankView
                rerank={investigation.llmRerank}
            />
            {investigation.mapRoute ? (
                <CodebaseWikiInvestigationMapRouteView
                    mapRoute={investigation.mapRoute}
                />
            ) : null}
            <CodebaseWikiEvidencePathView steps={investigation.evidencePath} />
            <CodebaseWikiInvestigationCitationList
                citations={investigation.citations}
            />
            <CodebaseWikiContextPageList
                contextPages={investigation.contextPages}
            />
            <CodebaseWikiReferenceList
                label="Key files"
                references={investigation.keyFiles}
            />
            <CodebaseWikiList
                label="Key symbols"
                values={investigation.keySymbols.map(
                    formatCodebaseWikiLocalizationSymbol,
                )}
            />
            {investigation.keyOutlines.length > 0 ? (
                <section className="space-y-2">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Key outlines
                    </h6>
                    <ol className="space-y-2">
                        {investigation.keyOutlines.map((outline, index) => (
                            <CodebaseWikiOutlineView
                                key={`investigation-outline-${codebaseWikiOutlineKey(outline, index)}`}
                                outline={outline}
                            />
                        ))}
                    </ol>
                </section>
            ) : null}
            <CodebaseWikiReferenceList
                label="Related tests"
                references={investigation.relatedTests}
            />
            {investigation.candidateLocations.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Source-backed candidates
                    </h6>
                    <ol className="space-y-2">
                        {investigation.candidateLocations.map(
                            (candidate, index) => (
                                <CodebaseWikiLocalizationCandidateView
                                    key={`investigation-candidate-${candidate.path}-${index}`}
                                    candidate={candidate}
                                    index={index}
                                />
                            ),
                        )}
                    </ol>
                </div>
            ) : null}
            {investigation.lowConfidenceCandidates.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Low-confidence candidates
                    </h6>
                    <ol className="space-y-2">
                        {investigation.lowConfidenceCandidates.map(
                            (candidate, index) => (
                                <CodebaseWikiLocalizationCandidateView
                                    key={`investigation-low-candidate-${candidate.path}-${index}`}
                                    candidate={candidate}
                                    index={index}
                                />
                            ),
                        )}
                    </ol>
                </div>
            ) : null}
            {investigation.knowledgeDocuments.length > 0 ? (
                <section className="space-y-2">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Knowledge documents
                    </h6>
                    <ol className="space-y-2">
                        {investigation.knowledgeDocuments.map(
                            (match, index) => (
                                <CodebaseWikiKnowledgeMatchView
                                    key={`investigation-knowledge-${codebaseWikiKnowledgeMatchKey(match, index)}`}
                                    match={match}
                                />
                            ),
                        )}
                    </ol>
                </section>
            ) : null}
            <CodebaseWikiList
                label="Source-backed findings"
                values={investigation.sourceBackedFindings}
            />
            <CodebaseWikiList
                label="Knowledge-only leads"
                values={investigation.knowledgeOnlyLeads}
            />
            <CodebaseWikiList
                label="Hypotheses"
                values={investigation.hypotheses}
            />
            <CodebaseWikiList
                label="Missing context"
                values={investigation.missingContext}
            />
            <CodebaseWikiList
                label="Suggested next reads"
                values={investigation.suggestedNextReads}
            />
            <CodebaseWikiLocalizationRuledOutList
                ruledOut={investigation.ruledOut}
            />
            {investigation.searchTrail.length > 0 ? (
                <details className="group">
                    <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                        Search trail
                    </summary>
                    <ol className="border-border/60 mt-1.5 space-y-1.5 border-l pl-3">
                        {investigation.searchTrail.map((step, index) => (
                            <CodebaseWikiLocalizationTrailStepView
                                key={`investigation-trail-${step.operation}-${step.query}-${index}`}
                                step={step}
                            />
                        ))}
                    </ol>
                </details>
            ) : null}
        </section>
    );
}

function CodebaseWikiInvestigationRerankView({
    rerank,
}: {
    readonly rerank: CodebaseWikiInvestigationRerank | null;
}) {
    if (!rerank) {
        return null;
    }
    const usage = Object.entries(rerank.llmUsage).map(
        ([key, value]) => `${key}: ${value}`,
    );
    return (
        <section className="border-border/50 bg-muted/20 space-y-2 rounded border px-2 py-1.5">
            <div className="space-y-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h6 className="text-foreground text-xs font-medium">
                        Bounded LLM rerank
                    </h6>
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(rerank.status)}
                        {' · '}
                        {renderUntrustedString(rerank.confidence)}
                        {' · '}
                        {renderUntrustedString(rerank.rerankModel)}
                    </span>
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {renderUntrustedString(rerank.summary)}
                </p>
            </div>
            {rerank.likelyFiles.length > 0 ? (
                <ol className="space-y-1.5">
                    {rerank.likelyFiles.map((file, index) => (
                        <li
                            key={`investigation-rerank-file-${file.path}-${index}`}
                            className="text-muted-foreground text-xs leading-relaxed"
                        >
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <span className="text-foreground font-mono break-all">
                                    {renderUntrustedString(file.path)}
                                </span>
                                <span className="text-muted-foreground/70 text-[11px]">
                                    {renderUntrustedString(file.confidence)}
                                </span>
                            </div>
                            <p>{renderUntrustedString(file.reason)}</p>
                            <CodebaseWikiList
                                label="Citations"
                                values={file.evidenceCitationIds}
                            />
                            <CodebaseWikiList
                                label="Symbols"
                                values={file.symbols}
                            />
                        </li>
                    ))}
                </ol>
            ) : null}
            <CodebaseWikiList
                label="Likely symbols"
                values={rerank.likelySymbols}
            />
            <CodebaseWikiList
                label="Tests to read"
                values={rerank.testsToRead}
            />
            <CodebaseWikiList label="Ruled out" values={rerank.ruledOut} />
            <CodebaseWikiList
                label="Suggested next reads"
                values={rerank.suggestedNextReads}
            />
            <CodebaseWikiList label="LLM usage" values={usage} />
            <CodebaseWikiList label="Warnings" values={rerank.warnings} />
            <CodebaseWikiEvidenceReviewCitationIndex
                citations={rerank.citationIndex}
            />
        </section>
    );
}

function CodebaseWikiEvidenceReviewView({
    review,
}: {
    readonly review: CodebaseWikiEvidenceReview;
}) {
    return (
        <section className="border-border/60 space-y-2 border-t pt-2.5">
            <div className="space-y-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h5 className="text-foreground text-xs font-medium">
                        LLM evidence review
                    </h5>
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(review.verdict)}
                        {' · '}
                        {renderUntrustedString(review.confidence)}
                        {' · '}
                        {renderUntrustedString(review.reviewModel)}
                    </span>
                </div>
                <p className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(review.question)}
                </p>
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {renderUntrustedString(review.summary)}
                </p>
            </div>
            {review.findings.length > 0 ? (
                <ol className="space-y-2">
                    {review.findings.map((finding, index) => (
                        <li
                            key={`evidence-review-finding-${finding.title}-${index}`}
                            className="border-border/50 bg-muted/20 rounded border px-2 py-1.5"
                        >
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <h6 className="text-foreground text-xs font-medium">
                                    {renderUntrustedString(finding.title)}
                                </h6>
                                <span className="text-muted-foreground/70 text-[11px]">
                                    {renderUntrustedString(finding.severity)}
                                    {' · '}
                                    {renderUntrustedString(finding.category)}
                                    {' · '}
                                    {renderUntrustedString(finding.confidence)}
                                </span>
                            </div>
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                                {renderUntrustedString(finding.claim)}
                            </p>
                            <CodebaseWikiList
                                label="Citations"
                                values={finding.evidenceCitationIds}
                            />
                            <CodebaseWikiList
                                label="Affected paths"
                                values={finding.affectedPaths}
                            />
                            {finding.nextStep ? (
                                <p className="text-muted-foreground/80 mt-1 text-[11px] leading-relaxed">
                                    Next:{' '}
                                    {renderUntrustedString(finding.nextStep)}
                                </p>
                            ) : null}
                        </li>
                    ))}
                </ol>
            ) : null}
            <CodebaseWikiList
                label="Missing context"
                values={review.missingContext}
            />
            <CodebaseWikiList
                label="Suggested next reads"
                values={review.suggestedNextReads}
            />
            <CodebaseWikiEvidenceReviewCitationIndex
                citations={review.citationIndex}
            />
            <CodebaseWikiList label="Warnings" values={review.warnings} />
        </section>
    );
}

function CodebaseWikiEvidenceReviewCitationIndex({
    citations,
}: {
    readonly citations: ReadonlyArray<CodebaseWikiEvidenceReviewCitation>;
}) {
    if (citations.length === 0) {
        return null;
    }
    return (
        <details className="group">
            <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                Citation index
            </summary>
            <ol className="border-border/60 mt-1.5 space-y-1.5 border-l pl-3">
                {citations.map((citation, index) => (
                    <li
                        key={`evidence-review-citation-${citation.citationId}-${index}`}
                        className="text-muted-foreground text-[11px] leading-relaxed"
                    >
                        <span className="text-foreground font-mono">
                            {renderUntrustedString(citation.citationId)}
                        </span>{' '}
                        <span>
                            {renderUntrustedString(citation.evidenceType)}
                        </span>
                        {': '}
                        <span className="font-mono break-all">
                            {renderUntrustedString(citation.label)}
                        </span>
                        {citation.path ? (
                            <span className="text-muted-foreground/70 font-mono break-all">
                                {' '}
                                {renderUntrustedString(citation.path)}
                            </span>
                        ) : null}
                        {citation.lineStart !== null ? (
                            <span className="text-muted-foreground/70">
                                {' '}
                                L{citation.lineStart}
                                {citation.lineEnd !== null
                                    ? `-${citation.lineEnd}`
                                    : ''}
                            </span>
                        ) : null}
                    </li>
                ))}
            </ol>
        </details>
    );
}

function CodebaseWikiInvestigationQueryPlanView({
    queryPlan,
}: {
    readonly queryPlan: CodebaseWikiInvestigationQueryPlan | null;
}) {
    if (!queryPlan) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                Deterministic query plan
            </h6>
            <p className="text-muted-foreground text-[11px] break-words">
                {renderUntrustedString(queryPlan.strategy)}
            </p>
            <CodebaseWikiList
                label="Traversal policy"
                values={queryPlan.traversalPolicy}
            />
            <CodebaseWikiList
                label="Primary tools"
                values={queryPlan.primaryTools}
            />
            <CodebaseWikiList
                label="Fallback tools"
                values={queryPlan.fallbackTools}
            />
        </div>
    );
}

function CodebaseWikiSourceReadBudgetView({
    budget,
}: {
    readonly budget: CodebaseWikiSourceReadBudget | null;
}) {
    if (!budget) {
        return null;
    }
    const values = [
        `next reads ${budget.dedupedNextReadCount ?? 0}/${budget.maxNextReads ?? 0}`,
        `candidates ${budget.candidateCount ?? 0}`,
        `outlines ${budget.outlineCount ?? 0}`,
        `source ranges ${budget.sourceRangeCount ?? 0}`,
        `map reads ${budget.mapReadCount ?? 0}`,
        `inline lines ${budget.inlineSourceLineCount ?? 0}/${budget.maxInlineSourceLines ?? 0}`,
        `context pages ${budget.contextPageCount ?? 0}`,
        `capped ${budget.capped ? 'yes' : 'no'}`,
    ];
    return (
        <div className="space-y-1">
            <CodebaseWikiList label="Source-read budget" values={values} />
            <CodebaseWikiList
                label="Budget warnings"
                values={budget.warnings}
            />
        </div>
    );
}

function CodebaseWikiInvestigationQualitySignalsView({
    qualitySignals,
}: {
    readonly qualitySignals: CodebaseWikiInvestigationQualitySignals | null;
}) {
    if (!qualitySignals) {
        return null;
    }
    const values = [
        `route paths ${qualitySignals.hasRoutePaths ? 'present' : 'missing'}`,
        `evidence path ${qualitySignals.evidencePathCount ?? 0}`,
        `outlines ${qualitySignals.outlineCount ?? 0}`,
        `citations ${qualitySignals.citationCount ?? 0}`,
        `source-backed candidates ${qualitySignals.sourceBackedCandidateCount ?? 0}`,
        `index ${qualitySignals.indexValidationStatus ?? 'unknown'}`,
        `map warnings ${qualitySignals.mapWarningCount ?? 0}`,
        `missing context ${qualitySignals.missingContextCount ?? 0}`,
        `context pages ${qualitySignals.contextPageCount ?? 0}`,
        `runtime anchors ${qualitySignals.runtimeAnchorCount ?? 0}`,
        `runtime source refs ${qualitySignals.runtimeSourceReferenceCount ?? 0}`,
    ];
    return <CodebaseWikiList label="Quality signals" values={values} />;
}

function CodebaseWikiInvestigationMapRouteView({
    mapRoute,
}: {
    readonly mapRoute: CodebaseWikiInvestigationMapRoute;
}) {
    return (
        <section className="border-border/60 space-y-1.5 rounded border px-2.5 py-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <h6 className="text-muted-foreground text-[11px] font-medium">
                    Codebase map route
                </h6>
                <span className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(mapRoute.confidence)}
                </span>
            </div>
            <CodebaseWikiInvestigationMapMatchList
                label="Likely subsystems"
                matches={mapRoute.likelySubsystems}
            />
            <CodebaseWikiInvestigationMapMatchList
                label="Entry files"
                matches={mapRoute.entryFiles}
            />
            <CodebaseWikiInvestigationMapMatchList
                label="Symbols"
                matches={mapRoute.symbols}
            />
            <CodebaseWikiInvestigationMapMatchList
                label="Map tests"
                matches={mapRoute.tests}
            />
            <CodebaseWikiInvestigationMapMatchList
                label="Map docs"
                matches={mapRoute.docs}
            />
            <CodebaseWikiList
                label="Map suggested reads"
                values={mapRoute.suggestedNextReads.map(
                    formatCodebaseWikiInvestigationMapNextRead,
                )}
            />
            <CodebaseWikiMapRoutePathList routePaths={mapRoute.routePaths} />
            <CodebaseWikiMapSummaryList
                summaries={mapRoute.structuralSummaries}
            />
            <CodebaseWikiIndexValidationView
                validation={mapRoute.indexValidation}
            />
            <CodebaseWikiList label="Map warnings" values={mapRoute.warnings} />
        </section>
    );
}

function CodebaseWikiMapRoutePathList({
    routePaths,
}: {
    readonly routePaths: ReadonlyArray<CodebaseWikiInvestigationMapRoutePath>;
}) {
    if (routePaths.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h6 className="text-muted-foreground/70 text-[11px]">
                Route paths
            </h6>
            <ol className="space-y-1.5">
                {routePaths.map((routePath, index) => (
                    <li
                        key={`route-path-${routePath.label}-${index}`}
                        className="text-muted-foreground text-[11px]"
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-foreground font-mono break-all">
                                {renderUntrustedString(routePath.label)}
                            </span>
                            <span className="text-muted-foreground/70">
                                {renderUntrustedString(routePath.confidence)}
                            </span>
                        </div>
                        <ol className="border-border/60 mt-1 space-y-0.5 border-l pl-2">
                            {routePath.hops.map((hop, hopIndex) => (
                                <li
                                    key={`route-hop-${routePath.label}-${hop.label}-${hopIndex}`}
                                    className="break-words"
                                >
                                    <span className="font-mono">
                                        {renderUntrustedString(hop.label)}
                                    </span>
                                    {hop.path ? (
                                        <span className="text-muted-foreground/70 font-mono">
                                            {' '}
                                            {renderUntrustedString(hop.path)}
                                        </span>
                                    ) : null}
                                </li>
                            ))}
                        </ol>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiMapSummaryList({
    summaries,
}: {
    readonly summaries: ReadonlyArray<CodebaseWikiInvestigationMapSummary>;
}) {
    if (summaries.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h6 className="text-muted-foreground/70 text-[11px]">
                Structural summaries
            </h6>
            <ol className="space-y-1">
                {summaries.map((summary, index) => (
                    <li
                        key={`map-summary-${summary.label}-${index}`}
                        className="text-muted-foreground text-[11px] leading-relaxed"
                    >
                        <span className="text-foreground font-medium">
                            {renderUntrustedString(summary.label)}
                        </span>
                        {': '}
                        <span>{renderUntrustedString(summary.summary)}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiIndexValidationView({
    validation,
}: {
    readonly validation: CodebaseWikiIndexValidation | null;
}) {
    if (!validation) {
        return null;
    }
    const values = [
        `status ${validation.status}`,
        `nodes ${validation.nodeCount ?? 0}`,
        `edges ${validation.edgeCount ?? 0}`,
        `summaries ${validation.summaryCount ?? 0}`,
        `warnings ${validation.warningCount ?? 0}`,
    ];
    return (
        <div className="space-y-1">
            <CodebaseWikiList label="Index validation" values={values} />
            <CodebaseWikiList
                label="Index warnings"
                values={validation.warnings}
            />
        </div>
    );
}

function CodebaseWikiEvidencePathView({
    steps,
}: {
    readonly steps: ReadonlyArray<CodebaseWikiEvidencePathStep>;
}) {
    if (steps.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1.5">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                Evidence path
            </h6>
            <ol className="space-y-1">
                {steps.map((step, index) => (
                    <li
                        key={`evidence-path-${step.stage}-${step.label}-${index}`}
                        className="text-muted-foreground text-[11px] leading-relaxed"
                    >
                        <span className="text-foreground">
                            {renderUntrustedString(step.stage)}
                        </span>
                        <span className="text-muted-foreground/70">
                            {' '}
                            via {renderUntrustedString(step.tool)}
                        </span>
                        {': '}
                        <span className="font-mono break-all">
                            {renderUntrustedString(step.label)}
                        </span>
                        {step.path ? (
                            <span className="text-muted-foreground/70 font-mono break-all">
                                {' '}
                                {renderUntrustedString(step.path)}
                            </span>
                        ) : null}
                        {step.lineStart !== null ? (
                            <span className="text-muted-foreground/70">
                                {' '}
                                L{step.lineStart}
                                {step.lineEnd !== null
                                    ? `-L${step.lineEnd}`
                                    : ''}
                            </span>
                        ) : null}
                        <span className="text-muted-foreground/70">
                            {' '}
                            {renderUntrustedString(step.reason)}
                        </span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiInvestigationCitationList({
    citations,
}: {
    readonly citations: ReadonlyArray<CodebaseWikiInvestigationCitation>;
}) {
    if (citations.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1.5">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                Evidence citations
            </h6>
            <ol className="space-y-1">
                {citations.map((citation, index) => (
                    <li
                        key={`citation-${citation.evidenceType}-${citation.label}-${index}`}
                        className="text-muted-foreground text-[11px] leading-relaxed"
                    >
                        <span className="text-foreground">
                            {renderUntrustedString(citation.evidenceType)}
                        </span>
                        {': '}
                        <span className="font-mono break-all">
                            {renderUntrustedString(citation.label)}
                        </span>
                        {citation.path ? (
                            <span className="text-muted-foreground/70 font-mono break-all">
                                {' '}
                                {renderUntrustedString(citation.path)}
                            </span>
                        ) : null}
                        {citation.lineStart !== null ? (
                            <span className="text-muted-foreground/70">
                                {' '}
                                L{citation.lineStart}
                                {citation.lineEnd !== null
                                    ? `-L${citation.lineEnd}`
                                    : ''}
                            </span>
                        ) : null}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiContextPageList({
    contextPages,
}: {
    readonly contextPages: ReadonlyArray<CodebaseWikiContextPage>;
}) {
    if (contextPages.length === 0) {
        return null;
    }
    return (
        <details className="group" open={true}>
            <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                Context pages
            </summary>
            <ol className="mt-1.5 space-y-1.5">
                {contextPages.map((page, index) => (
                    <li
                        key={`codebase-context-page-${page.pageId}-${page.path}-${index}`}
                        className="border-border/60 space-y-1 border-l pl-3"
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="text-foreground font-mono text-xs break-all">
                                {renderUntrustedString(page.pageId)}
                            </span>
                            <span className="text-muted-foreground/80 font-mono text-xs break-all">
                                {renderUntrustedString(page.path)}
                            </span>
                            {page.lineStart !== null ? (
                                <span className="text-muted-foreground/70 text-[11px]">
                                    L{page.lineStart}
                                    {page.lineEnd !== null
                                        ? `-L${page.lineEnd}`
                                        : ''}
                                </span>
                            ) : null}
                            {page.lineCount !== null && page.lineCount > 0 ? (
                                <span className="text-muted-foreground/70 text-[11px]">
                                    {page.lineCount} lines
                                </span>
                            ) : null}
                            {page.sourceUrl ? (
                                <a
                                    className="text-primary text-[11px] underline-offset-2 hover:underline"
                                    href={page.sourceUrl}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    source
                                </a>
                            ) : null}
                        </div>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                            {renderUntrustedString(page.reason)}
                        </p>
                        <code className="bg-muted/40 text-muted-foreground block overflow-x-auto rounded border px-2 py-1 font-mono text-[11px] whitespace-pre">
                            {renderUntrustedString(page.readCommand)}
                        </code>
                    </li>
                ))}
            </ol>
        </details>
    );
}

function CodebaseWikiInvestigationMapMatchList({
    label,
    matches,
}: {
    readonly label: string;
    readonly matches: ReadonlyArray<CodebaseWikiInvestigationMapMatch>;
}) {
    if (matches.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h6 className="text-muted-foreground/70 text-[11px]">{label}</h6>
            <ol className="space-y-1">
                {matches.map((match, index) => (
                    <li
                        key={`${label}-${match.label}-${match.path ?? 'map'}-${index}`}
                        className="text-muted-foreground text-[11px] leading-relaxed"
                    >
                        <span className="text-foreground font-mono">
                            {renderUntrustedString(match.label)}
                        </span>
                        {match.path ? (
                            <React.Fragment>
                                <span className="text-muted-foreground/40">
                                    {' · '}
                                </span>
                                <span className="font-mono">
                                    {renderUntrustedString(match.path)}
                                </span>
                            </React.Fragment>
                        ) : null}
                        {match.reasons[0] ? (
                            <React.Fragment>
                                <span className="text-muted-foreground/40">
                                    {': '}
                                </span>
                                <span>
                                    {renderUntrustedString(match.reasons[0])}
                                </span>
                            </React.Fragment>
                        ) : null}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function formatCodebaseWikiInvestigationMapNextRead(
    read: CodebaseWikiInvestigationMapNextRead,
): string {
    const lineRange =
        read.path !== null && read.lineStart !== null && read.lineEnd !== null
            ? `:${read.lineStart}-${read.lineEnd}`
            : '';
    const targets = [
        read.path === null ? null : `${read.path}${lineRange}`,
        read.query === null ? null : `query=${read.query}`,
    ].filter((target) => target !== null);
    const targetText = targets.length > 0 ? ` ${targets.join(' ')}` : '';
    return `${read.tool}${targetText}: ${read.reason}`.trim();
}

function CodebaseWikiRuntimeHandoffView({
    handoff,
}: {
    readonly handoff: CodebaseWikiRuntimeEvidenceHandoff;
}) {
    const sourceBackedAnchors = handoff.anchors.filter(
        (anchor) => anchor.sourceBacked,
    );
    const hypothesisAnchors = handoff.anchors.filter(
        (anchor) => !anchor.sourceBacked,
    );
    return (
        <section className="border-border/60 space-y-2 border-t pt-2.5">
            <div className="space-y-1">
                <h5 className="text-foreground text-xs font-medium">
                    Runtime-to-code handoff
                </h5>
                <CodebaseWikiList
                    label="Evidence references"
                    values={handoff.evidenceReferences}
                />
            </div>
            {handoff.lookups.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Evidence lookups
                    </h6>
                    <ol className="space-y-1.5">
                        {handoff.lookups.map((lookup, index) => (
                            <CodebaseWikiRuntimeLookupView
                                key={`${lookup.reference}-${index}`}
                                lookup={lookup}
                            />
                        ))}
                    </ol>
                </div>
            ) : null}
            <CodebaseWikiRuntimeAnchorList
                anchors={sourceBackedAnchors}
                label="Source-backed anchors"
            />
            <CodebaseWikiList
                label="Source path candidates"
                values={handoff.sourcePathCandidates}
            />
            <CodebaseWikiRuntimeAnchorList
                anchors={hypothesisAnchors}
                label="Hypotheses"
            />
            <CodebaseWikiList
                label="Unsupported references"
                values={handoff.unsupportedReferences}
            />
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Localization input
                </summary>
                <pre className="bg-muted/40 text-muted-foreground mt-1.5 overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                    {renderUntrustedString(handoff.localizationQuery)}
                </pre>
            </details>
        </section>
    );
}

function CodebaseWikiRuntimeLookupView({
    lookup,
}: {
    readonly lookup: CodebaseWikiRuntimeEvidenceLookup;
}) {
    const metadata = [
        lookup.kind,
        lookup.status,
        lookup.environment,
        lookup.sourceTool,
    ].filter((value): value is string => Boolean(value));
    return (
        <li className="border-border/60 space-y-1 border-l pl-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground font-mono text-xs break-all">
                    {renderUntrustedString(lookup.reference)}
                </span>
                <span className="text-muted-foreground/70 text-[11px]">
                    {metadata.map((value, index) => (
                        <React.Fragment key={`${value}-${index}`}>
                            {index > 0 ? ', ' : null}
                            {renderUntrustedString(value)}
                        </React.Fragment>
                    ))}
                </span>
            </div>
            <CodebaseWikiList
                label="Identifiers"
                values={[
                    ...(lookup.identifier ? [lookup.identifier] : []),
                    ...(lookup.extractedAnchorCount === null
                        ? []
                        : [`anchors=${lookup.extractedAnchorCount}`]),
                ]}
            />
            <CodebaseWikiList label="Notes" values={lookup.notes} />
        </li>
    );
}

function CodebaseWikiRuntimeAnchorList({
    anchors,
    label,
}: {
    readonly anchors: ReadonlyArray<CodebaseWikiRuntimeEvidenceAnchor>;
    readonly label: string;
}) {
    if (anchors.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1.5">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                {label}
            </h6>
            <ol className="space-y-1.5">
                {anchors.map((anchor, index) => (
                    <li
                        key={`${anchor.kind}-${anchor.value}-${index}`}
                        className="border-border/60 space-y-1 border-l pl-3"
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="text-foreground text-xs font-medium">
                                {renderUntrustedString(anchor.label)}
                            </span>
                            <span className="text-muted-foreground/70 text-[11px]">
                                {renderUntrustedString(anchor.kind)}
                                {' · '}
                                {renderUntrustedString(anchor.confidence)}
                            </span>
                        </div>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                            {renderUntrustedString(anchor.value)}
                        </p>
                        <CodebaseWikiList
                            label="Source"
                            values={[
                                anchor.sourceReference,
                                ...(anchor.citation ? [anchor.citation] : []),
                            ]}
                        />
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiLocalizationView({
    localization,
}: {
    readonly localization: CodebaseWikiLocalization;
}) {
    return (
        <section className="border-border/60 space-y-2 border-t pt-2.5">
            <div className="space-y-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h5 className="text-foreground text-xs font-medium">
                        Localized candidates
                    </h5>
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(localization.confidence)}
                    </span>
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {renderUntrustedString(localization.summary)}
                </p>
                <CodebaseWikiList
                    label="Evidence references"
                    values={localization.evidenceReferences}
                />
            </div>
            {localization.candidateLocations.length > 0 ? (
                <ol className="space-y-2">
                    {localization.candidateLocations.map((candidate, index) => (
                        <CodebaseWikiLocalizationCandidateView
                            key={`candidate-${candidate.path}-${index}`}
                            candidate={candidate}
                            index={index}
                        />
                    ))}
                </ol>
            ) : null}
            {localization.lowConfidenceCandidates.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Low-confidence candidates
                    </h6>
                    <ol className="space-y-2">
                        {localization.lowConfidenceCandidates.map(
                            (candidate, index) => (
                                <CodebaseWikiLocalizationCandidateView
                                    key={`low-candidate-${candidate.path}-${index}`}
                                    candidate={candidate}
                                    index={index}
                                />
                            ),
                        )}
                    </ol>
                </div>
            ) : null}
            <CodebaseWikiReferenceList
                label="Related tests"
                references={localization.testReferences}
            />
            <CodebaseWikiList
                label="Source-backed findings"
                values={localization.sourceBackedFindings}
            />
            <CodebaseWikiList
                label="Hypotheses"
                values={localization.hypotheses}
            />
            <CodebaseWikiLocalizationRuledOutList
                ruledOut={localization.ruledOut}
            />
            {localization.searchTrail.length > 0 ? (
                <details className="group">
                    <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                        Search trail
                    </summary>
                    <ol className="border-border/60 mt-1.5 space-y-1.5 border-l pl-3">
                        {localization.searchTrail.map((step, index) => (
                            <CodebaseWikiLocalizationTrailStepView
                                key={`${step.operation}-${step.query}-${index}`}
                                step={step}
                            />
                        ))}
                    </ol>
                </details>
            ) : null}
        </section>
    );
}

function CodebaseWikiLocalizationRuledOutList({
    ruledOut,
}: {
    readonly ruledOut: ReadonlyArray<CodebaseWikiLocalizationRuledOut>;
}) {
    if (ruledOut.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                Ruled out
            </h6>
            <ol className="space-y-1">
                {ruledOut.map((item, index) => (
                    <li
                        key={`${item.path ?? 'ruled-out'}-${index}`}
                        className="text-muted-foreground text-[11px]"
                    >
                        {item.path ? (
                            <span className="text-foreground font-mono break-all">
                                {renderUntrustedString(item.path)}
                            </span>
                        ) : null}
                        {item.reason ? (
                            <React.Fragment>
                                {item.path ? ' ' : null}
                                <span>
                                    {renderUntrustedString(item.reason)}
                                </span>
                            </React.Fragment>
                        ) : null}
                        {item.evidence.length > 0 ? (
                            <CodebaseWikiList
                                label="Evidence"
                                values={item.evidence}
                            />
                        ) : null}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiLocalizationCandidateView({
    candidate,
    index,
}: {
    readonly candidate: CodebaseWikiLocalizationCandidate;
    readonly index: number;
}) {
    const scoreLabel =
        candidate.score === null
            ? candidate.confidence
            : `${candidate.confidence} · score ${formatCodebaseLocalizationScore(candidate.score)}`;
    return (
        <li className="border-border/60 space-y-1.5 border-l pl-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-muted-foreground/70 text-[11px]">
                    #{index + 1}
                </span>
                <span className="text-foreground min-w-0 font-mono text-xs break-all">
                    {renderUntrustedString(candidate.path)}
                </span>
                <span className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(scoreLabel)}
                </span>
            </div>
            <CodebaseWikiList label="Reasons" values={candidate.reasons} />
            <CodebaseWikiList
                label="Symbols"
                values={candidate.symbols.map(
                    formatCodebaseWikiLocalizationSymbol,
                )}
            />
            <CodebaseWikiList
                label="Relationships"
                values={candidate.relationships.map(
                    formatCodebaseWikiLocalizationRelationship,
                )}
            />
            <CodebaseWikiReferenceList
                label="Tests"
                references={candidate.testReferences}
            />
            <CodebaseWikiList
                label="Findings"
                values={candidate.sourceBackedFindings}
            />
            <CodebaseWikiList
                label="Hypotheses"
                values={candidate.hypotheses}
            />
            {candidate.sourceRanges.length > 0 ? (
                <div className="space-y-1.5">
                    <h6 className="text-muted-foreground text-[11px] font-medium">
                        Source ranges
                    </h6>
                    <div className="space-y-1.5">
                        {candidate.sourceRanges.map(
                            (sourceRange, rangeIndex) => (
                                <CodebaseWikiSourceRangeView
                                    key={`${sourceRange.path}-${sourceRange.lineStart}-${rangeIndex}`}
                                    sourceRange={sourceRange}
                                />
                            ),
                        )}
                    </div>
                </div>
            ) : null}
        </li>
    );
}

function CodebaseWikiSourceRangeView({
    sourceRange,
}: {
    readonly sourceRange: CodebaseWikiSourceRange;
}) {
    return (
        <div className="space-y-1">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-[11px]">
                <span className="text-foreground font-mono break-all">
                    {renderUntrustedString(sourceRange.path)}
                </span>
                <span className="text-muted-foreground/70">
                    L{sourceRange.lineStart}-L{sourceRange.lineEnd}
                </span>
                {sourceRange.sourceUrl ? (
                    <a
                        className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                        href={sourceRange.sourceUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        source
                    </a>
                ) : null}
            </div>
            <div className="bg-muted/40 text-muted-foreground overflow-x-auto rounded border px-2 py-1.5 font-mono text-[11px] leading-relaxed">
                {sourceRange.lines.map((line) => (
                    <div
                        key={`${sourceRange.path}-${line.lineNumber}`}
                        className="grid grid-cols-[3rem_minmax(0,1fr)] gap-2"
                    >
                        <span className="text-muted-foreground/50 text-right select-none">
                            {line.lineNumber}
                        </span>
                        <span className="min-w-0 break-words whitespace-pre-wrap">
                            {renderUntrustedString(line.text)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CodebaseWikiLocalizationTrailStepView({
    step,
}: {
    readonly step: CodebaseWikiLocalizationTrailStep;
}) {
    const resultLabel =
        step.resultCount === null
            ? null
            : `${step.resultCount} ${step.resultCount === 1 ? 'result' : 'results'}`;
    return (
        <li className="space-y-1 text-[11px]">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground font-medium">
                    {renderUntrustedString(step.operation.replace(/_/g, ' '))}
                </span>
                {resultLabel ? (
                    <span className="text-muted-foreground/70">
                        {resultLabel}
                    </span>
                ) : null}
                {step.path ? (
                    <span className="text-muted-foreground font-mono break-all">
                        {renderUntrustedString(step.path)}
                    </span>
                ) : null}
            </div>
            {step.query ? (
                <p className="text-muted-foreground break-words">
                    {renderUntrustedString(step.query)}
                </p>
            ) : null}
            <CodebaseWikiList label="Inspected" values={step.inspectedPaths} />
            <CodebaseWikiList label="Notes" values={step.notes} />
        </li>
    );
}

function CodebaseWikiSubsystemView({
    subsystem,
}: {
    readonly subsystem: CodebaseWikiContextSubsystem;
}) {
    return (
        <li className="border-border/60 space-y-1 border-l pl-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground text-xs font-medium">
                    {renderUntrustedString(subsystem.name)}
                </span>
                <span className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(subsystem.confidence)}
                </span>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed">
                {renderUntrustedString(subsystem.description)}
            </p>
            <CodebaseWikiList label="Reasons" values={subsystem.reasons} />
            <CodebaseWikiList label="Sources" values={subsystem.sourcePaths} />
        </li>
    );
}

function CodebaseWikiReferenceList({
    label,
    references,
}: {
    readonly label: string;
    readonly references: ReadonlyArray<CodebaseWikiContextReference>;
}) {
    if (references.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h6 className="text-muted-foreground text-[11px] font-medium">
                {label}
            </h6>
            <ol className="space-y-1">
                {references.map((reference) => (
                    <li
                        key={`${reference.kind}-${reference.path}`}
                        className="text-muted-foreground text-[11px]"
                    >
                        <span className="text-foreground font-mono break-all">
                            {renderUntrustedString(reference.path)}
                        </span>{' '}
                        <span>{renderUntrustedString(reference.reason)}</span>
                        {reference.sourceUrl ? (
                            <React.Fragment>
                                {' '}
                                <a
                                    className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                                    href={reference.sourceUrl}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    source
                                </a>
                            </React.Fragment>
                        ) : null}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function CodebaseWikiMetric({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="border-border/60 border-l pl-2.5">
            <div className="text-muted-foreground/70 text-[11px]">{label}</div>
            <div className="text-foreground min-w-0 break-words">
                {value || 'None'}
            </div>
        </div>
    );
}

function codebaseWikiMatchKey(match: CodebaseWikiMatch, index: number): string {
    return [
        match.path,
        match.lineStart ?? 'start',
        match.lineEnd ?? 'end',
        match.score ?? 'score',
        index,
    ].join(':');
}

function CodebaseWikiMatchView({
    match,
}: {
    readonly match: CodebaseWikiMatch;
}) {
    return (
        <li className="border-border/60 space-y-1.5 border-l pl-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground min-w-0 font-mono text-xs break-all">
                    {renderUntrustedString(match.path)}
                </span>
                {match.language ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(match.language)}
                    </span>
                ) : null}
                {match.reasons.length > 0 ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        {match.reasons
                            .map(renderUntrustedString)
                            .map((reason, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 ? ', ' : null}
                                    {reason}
                                </React.Fragment>
                            ))}
                    </span>
                ) : null}
                {match.sourceUrl ? (
                    <a
                        className="text-muted-foreground hover:text-foreground text-[11px] underline underline-offset-2"
                        href={match.sourceUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        {formatCodebaseSourceLabel(match)}
                    </a>
                ) : null}
            </div>
            <CodebaseWikiList label="Symbols" values={match.symbols} />
            <CodebaseWikiList label="Headings" values={match.headings} />
            <CodebaseWikiList label="Imports" values={match.imports} />
            {match.snippet ? (
                <pre className="bg-muted/40 text-muted-foreground overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                    {renderUntrustedString(match.snippet)}
                </pre>
            ) : null}
        </li>
    );
}

function codebaseWikiReferenceMatchKey(
    reference: CodebaseWikiReferenceMatch,
    index: number,
): string {
    return [
        reference.path,
        reference.lineStart ?? 'start',
        reference.symbolName,
        reference.referenceKind,
        index,
    ].join(':');
}

function CodebaseWikiReferenceMatchView({
    reference,
}: {
    readonly reference: CodebaseWikiReferenceMatch;
}) {
    return (
        <li className="border-border/60 space-y-1.5 border-l pl-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground min-w-0 font-mono text-xs break-all">
                    {renderUntrustedString(reference.path)}
                </span>
                <span className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(reference.referenceKind)}
                </span>
                <span className="text-muted-foreground/70 font-mono text-[11px]">
                    {renderUntrustedString(reference.symbolName)}
                </span>
                {reference.sourceUrl ? (
                    <a
                        className="text-muted-foreground hover:text-foreground text-[11px] underline underline-offset-2"
                        href={reference.sourceUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        {formatCodebaseSourceLabel(reference)}
                    </a>
                ) : null}
            </div>
            <CodebaseWikiList label="Reasons" values={reference.reasons} />
            {reference.readCommand ? (
                <CodebaseWikiList
                    label="Read"
                    values={[reference.readCommand]}
                />
            ) : null}
            {reference.lineText ? (
                <pre className="bg-muted/40 text-muted-foreground overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                    {renderUntrustedString(reference.lineText)}
                </pre>
            ) : null}
        </li>
    );
}

function codebaseWikiOutlineKey(
    outline: CodebaseWikiOutline,
    index: number,
): string {
    return [
        outline.path,
        outline.language,
        outline.lineCount ?? 'lines',
        outline.score ?? 'score',
        index,
    ].join(':');
}

function CodebaseWikiOutlineView({
    outline,
}: {
    readonly outline: CodebaseWikiOutline;
}) {
    const visibleSymbols = outline.symbols.slice(0, 12);
    return (
        <li className="border-border/60 space-y-1.5 border-l pl-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground min-w-0 font-mono text-xs break-all">
                    {renderUntrustedString(outline.path)}
                </span>
                <span className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(outline.language)}
                </span>
                {outline.lineCount !== null ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        {outline.lineCount} lines
                    </span>
                ) : null}
                {outline.reasons.length > 0 ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        {outline.reasons
                            .map(renderUntrustedString)
                            .map((reason, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 ? ', ' : null}
                                    {reason}
                                </React.Fragment>
                            ))}
                    </span>
                ) : null}
                {outline.sourceUrl ? (
                    <a
                        className="text-muted-foreground hover:text-foreground text-[11px] underline underline-offset-2"
                        href={outline.sourceUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        source
                    </a>
                ) : null}
            </div>
            <CodebaseWikiList label="Roles" values={outline.roles} />
            <CodebaseWikiList label="Imports" values={outline.imports} />
            <CodebaseWikiList label="Headings" values={outline.headings} />
            {visibleSymbols.length > 0 ? (
                <div className="space-y-1">
                    <div className="text-muted-foreground/70 text-[11px]">
                        Symbols
                    </div>
                    <ol className="grid gap-1 md:grid-cols-2">
                        {visibleSymbols.map((symbol, index) => (
                            <CodebaseWikiOutlineSymbolView
                                key={`${symbol.name}-${symbol.lineStart ?? 'line'}-${index}`}
                                symbol={symbol}
                            />
                        ))}
                    </ol>
                </div>
            ) : null}
        </li>
    );
}

function CodebaseWikiOutlineSymbolView({
    symbol,
}: {
    readonly symbol: CodebaseWikiOutlineSymbol;
}) {
    return (
        <li className="bg-muted/30 min-w-0 rounded border px-2 py-1">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                <span className="text-foreground font-mono text-[11px] break-all">
                    {renderUntrustedString(symbol.name)}
                </span>
                <span className="text-muted-foreground/70 text-[10px]">
                    {renderUntrustedString(symbol.kind)}
                </span>
                {symbol.scopeDepth !== null && symbol.scopeDepth > 0 ? (
                    <span className="text-muted-foreground/70 text-[10px]">
                        depth {symbol.scopeDepth}
                    </span>
                ) : null}
                {symbol.childCount !== null && symbol.childCount > 0 ? (
                    <span className="text-muted-foreground/70 text-[10px]">
                        {symbol.childCount} children
                    </span>
                ) : null}
                {symbol.lineStart !== null ? (
                    <span className="text-muted-foreground/70 text-[10px]">
                        L{symbol.lineStart}
                        {symbol.lineEnd !== null &&
                        symbol.lineEnd !== symbol.lineStart
                            ? `-L${symbol.lineEnd}`
                            : ''}
                    </span>
                ) : null}
            </div>
            {symbol.signature ? (
                <div className="text-muted-foreground mt-0.5 font-mono text-[10px] break-all">
                    {renderUntrustedString(symbol.signature)}
                </div>
            ) : null}
            <CodebaseWikiList label="Decorators" values={symbol.decorators} />
            <CodebaseWikiList label="Tags" values={symbol.tags} />
        </li>
    );
}

function codebaseWikiKnowledgeMatchKey(
    match: CodebaseWikiKnowledgeMatch,
    index: number,
): string {
    return [
        match.path,
        match.lineStart ?? 'start',
        match.lineEnd ?? 'end',
        match.score ?? 'score',
        index,
    ].join(':');
}

function CodebaseWikiKnowledgeMatchView({
    match,
}: {
    readonly match: CodebaseWikiKnowledgeMatch;
}) {
    return (
        <li className="border-border/60 space-y-1.5 border-l pl-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground min-w-0 font-mono text-xs break-all">
                    {renderUntrustedString(match.path)}
                </span>
                <span className="text-muted-foreground/70 text-[11px]">
                    {renderUntrustedString(match.documentType)}
                </span>
                {match.sourceUrl ? (
                    <a
                        className="text-muted-foreground hover:text-foreground text-[11px] underline underline-offset-2"
                        href={match.sourceUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        {formatCodebaseKnowledgeSourceLabel(match)}
                    </a>
                ) : null}
            </div>
            <CodebaseWikiList label="Title" values={[match.title]} />
            <CodebaseWikiList label="Reasons" values={match.reasons} />
            {match.snippet ? (
                <pre className="bg-muted/40 text-muted-foreground overflow-x-auto rounded border px-2 py-1.5 text-[11px] leading-relaxed whitespace-pre-wrap">
                    {renderUntrustedString(match.snippet)}
                </pre>
            ) : null}
        </li>
    );
}

function CodebaseWikiList({
    label,
    values,
}: {
    readonly label: string;
    readonly values: ReadonlyArray<string>;
}) {
    if (values.length === 0) {
        return null;
    }
    return (
        <p className="text-muted-foreground text-[11px]">
            <span className="text-muted-foreground/70">{label}</span>{' '}
            {values.map((value, index) => (
                <React.Fragment key={`${label}-${value}-${index}`}>
                    {index > 0 ? (
                        <span className="text-muted-foreground/40">
                            {' · '}
                        </span>
                    ) : null}
                    <span className="font-mono">
                        {renderUntrustedString(value)}
                    </span>
                </React.Fragment>
            ))}
        </p>
    );
}

function formatCodebaseRevision({
    branch,
    revision,
}: {
    readonly branch: string | null;
    readonly revision: string | null;
}): string {
    const shortRevision = revision ? revision.slice(0, 12) : null;
    if (branch && shortRevision) {
        return `${branch} · ${shortRevision}`;
    }
    return branch ?? shortRevision ?? 'Unknown';
}

function formatCodebaseWikiConfig(wikiConfig: Record<string, unknown>): string {
    if (wikiConfig['present'] !== true) {
        return 'None';
    }
    const repoNoteCount = numberValue(wikiConfig['repo_note_count']) ?? 0;
    const pageCount = numberValue(wikiConfig['page_count']) ?? 0;
    const invalidReason = stringValue(wikiConfig['invalid_reason']);
    if (invalidReason) {
        return `Invalid: ${invalidReason}`;
    }
    return `${repoNoteCount} notes · ${pageCount} pages`;
}

function formatCodebaseIndexStatus(indexStatus: string | null): string {
    if (!indexStatus) {
        return 'Unknown';
    }
    return indexStatus.replace(/_/g, ' ');
}

function formatCodebaseSourceLabel(match: {
    readonly lineStart: number | null;
    readonly lineEnd: number | null;
}): string {
    if (match.lineStart === null) {
        return 'Source';
    }
    if (match.lineEnd === null || match.lineEnd === match.lineStart) {
        return `Source L${match.lineStart}`;
    }
    return `Source L${match.lineStart}-L${match.lineEnd}`;
}

function formatCodebaseKnowledgeSourceLabel(
    match: CodebaseWikiKnowledgeMatch,
): string {
    if (match.lineStart === null) {
        return 'Source';
    }
    if (match.lineEnd === null || match.lineEnd === match.lineStart) {
        return `Source L${match.lineStart}`;
    }
    return `Source L${match.lineStart}-L${match.lineEnd}`;
}

function formatCodebaseLocalizationScore(score: number): string {
    return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function formatCodebaseWikiLocalizationSymbol(
    symbol: CodebaseWikiLocalizationSymbol,
): string {
    const reasons =
        symbol.reasons.length > 0 ? ` (${symbol.reasons.join(', ')})` : '';
    return `${symbol.kind} ${symbol.name} in ${symbol.path}${reasons}`;
}

function formatCodebaseWikiLocalizationRelationship(
    relationship: CodebaseWikiLocalizationRelationship,
): string {
    const target = relationship.targetPath
        ? ` -> ${relationship.targetPath}`
        : '';
    const relationshipLabel = relationship.relationship
        ? `${relationship.kind}/${relationship.relationship}`
        : relationship.kind;
    const reasons =
        relationship.reasons.length > 0
            ? ` (${relationship.reasons.join(', ')})`
            : '';
    return `${relationshipLabel} ${relationship.importName}: ${relationship.sourcePath}${target}${reasons}`;
}

function safeCodebaseSourceUrl(value: unknown): string | null {
    const url = unwrapSingleUntrustedString(stringValue(value));
    if (!url?.startsWith('https://github.com/')) {
        return null;
    }
    return url;
}

function unwrapSingleUntrustedString(value: string | null): string | null {
    if (
        value?.startsWith(UNTRUSTED_START_MARKER) &&
        value.endsWith(UNTRUSTED_END_MARKER)
    ) {
        return value.slice(
            UNTRUSTED_START_MARKER.length,
            -UNTRUSTED_END_MARKER.length,
        );
    }
    return value;
}
function getCodebaseWikiDiagramEdges(
    mermaid: string,
): ReadonlyArray<CodebaseWikiDiagramEdge> {
    return stripUntrustedMarkers(mermaid)
        .split('\n')
        .map((line) => line.trim())
        .flatMap((line) => {
            const [from, to] = line.split(/\s+-->\s+/, 2);
            if (!from || !to) {
                return [];
            }
            return [
                {
                    from: from.trim(),
                    kind: null,
                    to: to.trim(),
                },
            ];
        });
}

function getCodebaseWikiDiagramEdgesFromPayload(
    value: unknown,
): ReadonlyArray<CodebaseWikiDiagramEdge> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((edge) => {
        const from = stringValue(edge['from_label']);
        const to = stringValue(edge['to_label']);
        if (!from || !to) {
            return [];
        }
        return [
            {
                from,
                kind: stringValue(edge['kind']),
                to,
            },
        ];
    });
}

function getCodebaseWikiPages(
    payload: AdminAgentArtifact['payload'],
): ReadonlyArray<CodebaseWikiPage> {
    return codebaseWikiPagesFromUnknown(toRecord(payload)['wiki_pages']);
}

function codebaseWikiPagesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiPage> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((page) => {
        const title = stringValue(page['title']);
        const purpose = stringValue(page['purpose']);
        if (!title || !purpose) {
            return [];
        }
        return [
            {
                title,
                purpose,
                parent: stringValue(page['parent']),
                sourcePaths: stringsFromUnknown(page['source_paths']),
                pageNotes: stringsFromUnknown(page['page_notes']),
            },
        ];
    });
}

function getCodebaseWikiPageContent(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiPageContent | null {
    const page = toRecord(payload)['wiki_page'];
    if (!isRecord(page)) {
        return null;
    }
    const title = stringValue(page['title']);
    const purpose = stringValue(page['purpose']);
    const content = stringValue(page['content']);
    if (!title || !purpose || !content) {
        return null;
    }
    return {
        title,
        purpose,
        content,
        parent: stringValue(page['parent']),
        sourcePaths: stringsFromUnknown(page['source_paths']),
        pageNotes: stringsFromUnknown(page['page_notes']),
        diagramMermaid: stringValue(page['diagram_mermaid']),
        diagramEdges: getCodebaseWikiDiagramEdgesFromPayload(
            page['diagram_edges'],
        ),
    };
}

function getCodebaseWikiAnswer(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiAnswer | null {
    const answer = toRecord(payload)['answer'];
    if (!isRecord(answer)) {
        return null;
    }
    const question = stringValue(answer['question']);
    const answerText = stringValue(answer['answer']);
    if (!question || !answerText) {
        return null;
    }
    return {
        question,
        answer: answerText,
        citations: stringsFromUnknown(answer['citations']),
    };
}

function getCodebaseWikiContextPack(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiContextPack | null {
    const contextPack = toRecord(payload)['context_pack'];
    if (!isRecord(contextPack)) {
        return null;
    }
    const task = stringValue(contextPack['task']);
    const summary = stringValue(contextPack['summary']);
    const handoffPrompt = stringValue(contextPack['handoff_prompt']);
    if (!task || !summary || !handoffPrompt) {
        return null;
    }
    return {
        task,
        summary,
        confidence: stringValue(contextPack['confidence']) ?? 'unknown',
        primarySubsystem: codebaseWikiSubsystemFromUnknown(
            contextPack['primary_subsystem'],
        ),
        relatedSubsystems: codebaseWikiSubsystemsFromUnknown(
            contextPack['related_subsystems'],
        ),
        wikiPages: codebaseWikiPagesFromUnknown(contextPack['wiki_pages']),
        sourceReferences: codebaseWikiContextReferencesFromUnknown(
            contextPack['source_references'],
        ),
        testReferences: codebaseWikiContextReferencesFromUnknown(
            contextPack['test_references'],
        ),
        documentationReferences: codebaseWikiContextReferencesFromUnknown(
            contextPack['documentation_references'],
        ),
        runtimeSignals: codebaseWikiRuntimeSignalsFromUnknown(
            contextPack['runtime_signals'],
        ),
        runtimeAnchors: codebaseWikiRuntimeAnchorsFromUnknown(
            contextPack['runtime_anchors'],
        ),
        codeRelationships: codebaseWikiContextRelationshipsFromUnknown(
            contextPack['code_relationships'],
        ),
        playbookSteps: stringsFromUnknown(contextPack['playbook_steps']),
        openQuestions: stringsFromUnknown(contextPack['open_questions']),
        handoffPrompt,
    };
}

function codebaseWikiSubsystemsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiContextSubsystem> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item) => {
        const subsystem = codebaseWikiSubsystemFromUnknown(item);
        return subsystem ? [subsystem] : [];
    });
}

function codebaseWikiSubsystemFromUnknown(
    value: unknown,
): CodebaseWikiContextSubsystem | null {
    if (!isRecord(value)) {
        return null;
    }
    const name = stringValue(value['name']);
    const description = stringValue(value['description']);
    if (!name || !description) {
        return null;
    }
    return {
        name,
        description,
        confidence: stringValue(value['confidence']) ?? 'unknown',
        reasons: stringsFromUnknown(value['reasons']),
        sourcePaths: stringsFromUnknown(value['source_paths']),
    };
}

function codebaseWikiContextReferencesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiContextReference> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((reference) => {
        const path = stringValue(reference['path']);
        const reason = stringValue(reference['reason']);
        if (!path || !reason) {
            return [];
        }
        return [
            {
                path,
                kind: stringValue(reference['kind']) ?? 'source',
                reason,
                sourceUrl: safeCodebaseSourceUrl(reference['source_url']),
            },
        ];
    });
}

function codebaseWikiRuntimeSignalsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiRuntimeSignal> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((signal) => {
        const name = stringValue(signal['name']);
        const purpose = stringValue(signal['purpose']);
        if (!name || !purpose) {
            return [];
        }
        return [
            {
                name,
                purpose,
                tools: stringsFromUnknown(signal['tools']),
                sourcePaths: stringsFromUnknown(signal['source_paths']),
            },
        ];
    });
}

function codebaseWikiRuntimeAnchorsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiRuntimeAnchor> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((anchor) => {
        const kind = stringValue(anchor['kind']);
        const identifier = stringValue(anchor['identifier']);
        const label = stringValue(anchor['label']);
        const recommendedTool = stringValue(anchor['recommended_tool']);
        const lookupHint = stringValue(anchor['lookup_hint']);
        const reason = stringValue(anchor['reason']);
        if (
            !kind ||
            !identifier ||
            !label ||
            !recommendedTool ||
            !lookupHint ||
            !reason
        ) {
            return [];
        }
        return [
            {
                kind,
                identifier,
                label,
                recommendedTool,
                lookupHint,
                reason,
                url: stringValue(anchor['url']),
                sourcePaths: stringsFromUnknown(anchor['source_paths']),
            },
        ];
    });
}

function codebaseWikiContextRelationshipsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiContextRelationship> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((relationship) => {
        const sourcePath = stringValue(relationship['source_path']);
        const targetPath = stringValue(relationship['target_path']);
        const reason = stringValue(relationship['reason']);
        if (!sourcePath || !targetPath || !reason) {
            return [];
        }
        return [
            {
                sourcePath,
                targetPath,
                relationship:
                    stringValue(relationship['relationship']) ?? 'related',
                reason,
                sourceUrl: safeCodebaseSourceUrl(relationship['source_url']),
                targetSourceUrl: safeCodebaseSourceUrl(
                    relationship['target_source_url'],
                ),
            },
        ];
    });
}

function getCodebaseWikiLocalization(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiLocalization | null {
    return codebaseWikiLocalizationFromUnknown(
        toRecord(payload)['localization'],
    );
}

function getCodebaseWikiInvestigation(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiInvestigation | null {
    const investigation = toRecord(payload)['investigation'];
    if (!isRecord(investigation)) {
        return null;
    }
    const summary = stringValue(investigation['summary']);
    if (!summary) {
        return null;
    }
    return {
        question: stringValue(investigation['question']) ?? '',
        mode: stringValue(investigation['mode']) ?? 'unknown',
        evidenceReferences: stringsFromUnknown(
            investigation['evidence_references'],
        ),
        summary,
        confidence: stringValue(investigation['confidence']) ?? 'unknown',
        mapRoute: codebaseWikiInvestigationMapRouteFromUnknown(
            investigation['map_route'],
        ),
        queryPlan: codebaseWikiInvestigationQueryPlanFromUnknown(
            investigation['query_plan'],
        ),
        citations: codebaseWikiInvestigationCitationsFromUnknown(
            investigation['citations'],
        ),
        contextPages: codebaseWikiContextPagesFromUnknown(
            investigation['context_pages'],
        ),
        sourceReadBudget: codebaseWikiSourceReadBudgetFromUnknown(
            investigation['source_read_budget'],
        ),
        qualitySignals: codebaseWikiInvestigationQualitySignalsFromUnknown(
            investigation['quality_signals'],
        ),
        llmRerank: codebaseWikiInvestigationRerankFromUnknown(
            investigation['llm_rerank'],
        ),
        investigationPlan: stringsFromUnknown(
            investigation['investigation_plan'],
        ),
        candidateLocations: codebaseWikiLocalizationCandidatesFromUnknown(
            investigation['candidate_locations'],
        ),
        lowConfidenceCandidates: codebaseWikiLocalizationCandidatesFromUnknown(
            investigation['low_confidence_candidates'],
        ),
        keyFiles: codebaseWikiContextReferencesFromUnknown(
            investigation['key_files'],
        ),
        keySymbols: codebaseWikiLocalizationSymbolsFromUnknown(
            investigation['key_symbols'],
        ),
        relatedTests: codebaseWikiContextReferencesFromUnknown(
            investigation['related_tests'],
        ),
        knowledgeDocuments: codebaseWikiKnowledgeMatchesFromUnknown(
            investigation['knowledge_documents'],
        ),
        keyOutlines: codebaseWikiOutlinesFromUnknown(
            investigation['key_outlines'],
        ),
        evidencePath: codebaseWikiEvidencePathFromUnknown(
            investigation['evidence_path'],
        ),
        sourceBackedFindings: stringsFromUnknown(
            investigation['source_backed_findings'],
        ),
        knowledgeOnlyLeads: stringsFromUnknown(
            investigation['knowledge_only_leads'],
        ),
        hypotheses: stringsFromUnknown(investigation['hypotheses']),
        ruledOut: codebaseWikiLocalizationRuledOutFromUnknown(
            investigation['ruled_out'],
        ),
        searchTrail: codebaseWikiLocalizationTrailFromUnknown(
            investigation['search_trail'],
        ),
        missingContext: stringsFromUnknown(investigation['missing_context']),
        suggestedNextReads: stringsFromUnknown(
            investigation['suggested_next_reads'],
        ),
    };
}

function codebaseWikiInvestigationRerankFromUnknown(
    value: unknown,
): CodebaseWikiInvestigationRerank | null {
    if (!isRecord(value)) {
        return null;
    }
    const summary = stringValue(value['summary']);
    if (!summary) {
        return null;
    }
    return {
        status: stringValue(value['status']) ?? 'unknown',
        rerankModel: stringValue(value['rerank_model']) ?? 'unknown',
        summary,
        confidence: stringValue(value['confidence']) ?? 'unknown',
        likelyFiles: codebaseWikiInvestigationRerankFilesFromUnknown(
            value['likely_files'],
        ),
        likelySymbols: stringsFromUnknown(value['likely_symbols']),
        testsToRead: stringsFromUnknown(value['tests_to_read']),
        ruledOut: stringsFromUnknown(value['ruled_out']),
        suggestedNextReads: stringsFromUnknown(value['suggested_next_reads']),
        citationIndex: codebaseWikiEvidenceReviewCitationsFromUnknown(
            value['citation_index'],
        ),
        warnings: stringsFromUnknown(value['warnings']),
        llmUsage: numericRecordFromUnknown(value['llm_usage']),
    };
}

function codebaseWikiInvestigationRerankFilesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationRerankFile> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((file) => {
        const path = stringValue(file['path']);
        const reason = stringValue(file['reason']);
        if (!path || !reason) {
            return [];
        }
        return [
            {
                path,
                reason,
                confidence: stringValue(file['confidence']) ?? 'unknown',
                evidenceCitationIds: stringsFromUnknown(
                    file['evidence_citation_ids'],
                ),
                symbols: stringsFromUnknown(file['symbols']),
            },
        ];
    });
}

function getCodebaseWikiEvidenceReview(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiEvidenceReview | null {
    const review = toRecord(payload)['evidence_review'];
    if (!isRecord(review)) {
        return null;
    }
    const summary = stringValue(review['summary']);
    if (!summary) {
        return null;
    }
    return {
        question: stringValue(review['question']) ?? '',
        reviewModel: stringValue(review['review_model']) ?? 'unknown',
        summary,
        verdict: stringValue(review['verdict']) ?? 'unknown',
        confidence: stringValue(review['confidence']) ?? 'unknown',
        findings: codebaseWikiEvidenceReviewFindingsFromUnknown(
            review['findings'],
        ),
        missingContext: stringsFromUnknown(review['missing_context']),
        suggestedNextReads: stringsFromUnknown(review['suggested_next_reads']),
        citationIndex: codebaseWikiEvidenceReviewCitationsFromUnknown(
            review['citation_index'],
        ),
        warnings: stringsFromUnknown(review['warnings']),
        llmUsage: numericRecordFromUnknown(review['llm_usage']),
    };
}

function codebaseWikiEvidenceReviewFindingsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiEvidenceReviewFinding> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((finding) => {
        const title = stringValue(finding['title']);
        const claim = stringValue(finding['claim']);
        if (!title || !claim) {
            return [];
        }
        return [
            {
                severity: stringValue(finding['severity']) ?? 'unknown',
                category: stringValue(finding['category']) ?? 'unknown',
                title,
                claim,
                evidenceCitationIds: stringsFromUnknown(
                    finding['evidence_citation_ids'],
                ),
                affectedPaths: stringsFromUnknown(finding['affected_paths']),
                confidence: stringValue(finding['confidence']) ?? 'unknown',
                nextStep: stringValue(finding['next_step']),
            },
        ];
    });
}

function codebaseWikiEvidenceReviewCitationsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiEvidenceReviewCitation> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((citation) => {
        const citationId = stringValue(citation['citation_id']);
        const label = stringValue(citation['label']);
        if (!citationId || !label) {
            return [];
        }
        return [
            {
                citationId,
                evidenceType:
                    stringValue(citation['evidence_type']) ?? 'unknown',
                label,
                reason: stringValue(citation['reason']) ?? '',
                confidence: stringValue(citation['confidence']),
                path: stringValue(citation['path']),
                lineStart: numberValue(citation['line_start']),
                lineEnd: numberValue(citation['line_end']),
                symbol: stringValue(citation['symbol']),
                sourceUrl: safeCodebaseSourceUrl(citation['source_url']),
            },
        ];
    });
}

function numericRecordFromUnknown(value: unknown): Record<string, number> {
    if (!isRecord(value)) {
        return {};
    }
    return Object.fromEntries(
        Object.entries(value).filter(
            (entry): entry is [string, number] => typeof entry[1] === 'number',
        ),
    );
}

function codebaseWikiInvestigationMapRouteFromUnknown(
    value: unknown,
): CodebaseWikiInvestigationMapRoute | null {
    if (!isRecord(value)) {
        return null;
    }
    return {
        confidence: stringValue(value['confidence']) ?? 'unknown',
        likelySubsystems: codebaseWikiInvestigationMapMatchesFromUnknown(
            value['likely_subsystems'],
        ),
        entryFiles: codebaseWikiInvestigationMapMatchesFromUnknown(
            value['entry_files'],
        ),
        symbols: codebaseWikiInvestigationMapMatchesFromUnknown(
            value['symbols'],
        ),
        tests: codebaseWikiInvestigationMapMatchesFromUnknown(value['tests']),
        docs: codebaseWikiInvestigationMapMatchesFromUnknown(value['docs']),
        suggestedNextReads: codebaseWikiInvestigationMapNextReadsFromUnknown(
            value['suggested_next_reads'],
        ),
        routePaths: codebaseWikiInvestigationMapRoutePathsFromUnknown(
            value['route_paths'],
        ),
        structuralSummaries: codebaseWikiInvestigationMapSummariesFromUnknown(
            value['structural_summaries'],
        ),
        indexValidation: codebaseWikiIndexValidationFromUnknown(
            value['index_validation'],
        ),
        warnings: stringsFromUnknown(value['warnings']),
    };
}

function codebaseWikiInvestigationMapMatchesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationMapMatch> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((match) => {
        const label = stringValue(match['label']);
        if (!label) {
            return [];
        }
        return [
            {
                label,
                nodeType: stringValue(match['node_type']) ?? 'unknown',
                path: stringValue(match['path']),
                reasons: stringsFromUnknown(match['reasons']),
            },
        ];
    });
}

function codebaseWikiInvestigationMapNextReadsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationMapNextRead> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((read) => {
        const tool = stringValue(read['tool']);
        const reason = stringValue(read['reason']);
        if (!tool || !reason) {
            return [];
        }
        return [
            {
                tool,
                reason,
                path: stringValue(read['path']),
                query: stringValue(read['query']),
                lineStart: numberValue(read['line_start']),
                lineEnd: numberValue(read['line_end']),
            },
        ];
    });
}

function codebaseWikiInvestigationMapRoutePathsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationMapRoutePath> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((routePath) => {
        const label = stringValue(routePath['label']);
        if (!label) {
            return [];
        }
        return [
            {
                label,
                confidence: stringValue(routePath['confidence']) ?? 'unknown',
                score: numberValue(routePath['score']),
                hops: codebaseWikiInvestigationMapRouteHopsFromUnknown(
                    routePath['hops'],
                ),
            },
        ];
    });
}

function codebaseWikiInvestigationMapRouteHopsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationMapRouteHop> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((hop) => {
        const label = stringValue(hop['label']);
        if (!label) {
            return [];
        }
        return [
            {
                label,
                nodeType: stringValue(hop['node_type']) ?? 'unknown',
                reason: stringValue(hop['reason']) ?? '',
                path: stringValue(hop['path']),
                lineStart: numberValue(hop['line_start']),
                lineEnd: numberValue(hop['line_end']),
                sourceUrl: safeCodebaseSourceUrl(hop['source_url']),
            },
        ];
    });
}

function codebaseWikiInvestigationMapSummariesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationMapSummary> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((summary) => {
        const label = stringValue(summary['label']);
        const summaryText = stringValue(summary['summary']);
        if (!label || !summaryText) {
            return [];
        }
        return [
            {
                label,
                summary: summaryText,
                sourcePaths: stringsFromUnknown(summary['source_paths']),
                entryFiles: stringsFromUnknown(summary['entry_files']),
                tests: stringsFromUnknown(summary['tests']),
                docs: stringsFromUnknown(summary['docs']),
                symbols: stringsFromUnknown(summary['symbols']),
            },
        ];
    });
}

function codebaseWikiIndexValidationFromUnknown(
    value: unknown,
): CodebaseWikiIndexValidation | null {
    if (!isRecord(value)) {
        return null;
    }
    return {
        status: stringValue(value['status']) ?? 'unknown',
        warningCount: numberValue(value['warning_count']),
        nodeCount: numberValue(value['node_count']),
        edgeCount: numberValue(value['edge_count']),
        summaryCount: numberValue(value['summary_count']),
        warnings: stringsFromUnknown(value['warnings']),
    };
}

function codebaseWikiInvestigationQueryPlanFromUnknown(
    value: unknown,
): CodebaseWikiInvestigationQueryPlan | null {
    if (!isRecord(value)) {
        return null;
    }
    const strategy = stringValue(value['strategy']);
    if (!strategy) {
        return null;
    }
    return {
        strategy,
        traversalPolicy: stringsFromUnknown(value['traversal_policy']),
        primaryTools: stringsFromUnknown(value['primary_tools']),
        fallbackTools: stringsFromUnknown(value['fallback_tools']),
    };
}

function codebaseWikiInvestigationCitationsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiInvestigationCitation> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((citation) => {
        const label = stringValue(citation['label']);
        if (!label) {
            return [];
        }
        return [
            {
                evidenceType:
                    stringValue(citation['evidence_type']) ?? 'unknown',
                label,
                reason: stringValue(citation['reason']) ?? '',
                confidence: stringValue(citation['confidence']),
                path: stringValue(citation['path']),
                lineStart: numberValue(citation['line_start']),
                lineEnd: numberValue(citation['line_end']),
                symbol: stringValue(citation['symbol']),
                sourceUrl: safeCodebaseSourceUrl(citation['source_url']),
            },
        ];
    });
}

function codebaseWikiContextPagesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiContextPage> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((page) => {
        const pageId = stringValue(page['page_id']);
        const path = stringValue(page['path']);
        const reason = stringValue(page['reason']);
        const readCommand = stringValue(page['read_command']);
        if (!pageId || !path || !reason || !readCommand) {
            return [];
        }
        return [
            {
                pageId,
                path,
                lineStart: numberValue(page['line_start']),
                lineEnd: numberValue(page['line_end']),
                lineCount: numberValue(page['line_count']),
                sourceUrl: safeCodebaseSourceUrl(page['source_url']),
                reason,
                readCommand,
            },
        ];
    });
}

function codebaseWikiEvidencePathFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiEvidencePathStep> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((step) => {
        const label = stringValue(step['label']);
        if (!label) {
            return [];
        }
        return [
            {
                stage: stringValue(step['stage']) ?? 'unknown',
                tool: stringValue(step['tool']) ?? 'unknown',
                label,
                reason: stringValue(step['reason']) ?? '',
                confidence: stringValue(step['confidence']),
                path: stringValue(step['path']),
                symbol: stringValue(step['symbol']),
                lineStart: numberValue(step['line_start']),
                lineEnd: numberValue(step['line_end']),
                sourceUrl: safeCodebaseSourceUrl(step['source_url']),
            },
        ];
    });
}

function codebaseWikiSourceReadBudgetFromUnknown(
    value: unknown,
): CodebaseWikiSourceReadBudget | null {
    if (!isRecord(value)) {
        return null;
    }
    return {
        maxNextReads: numberValue(value['max_next_reads']),
        candidateCount: numberValue(value['candidate_count']),
        outlineCount: numberValue(value['outline_count']),
        sourceRangeCount: numberValue(value['source_range_count']),
        mapReadCount: numberValue(value['map_read_count']),
        inlineSourceLineCount: numberValue(value['inline_source_line_count']),
        maxInlineSourceLines: numberValue(value['max_inline_source_lines']),
        contextPageCount: numberValue(value['context_page_count']),
        dedupedNextReadCount: numberValue(value['deduped_next_read_count']),
        capped: booleanValue(value['capped']) ?? false,
        warnings: stringsFromUnknown(value['warnings']),
    };
}

function codebaseWikiInvestigationQualitySignalsFromUnknown(
    value: unknown,
): CodebaseWikiInvestigationQualitySignals | null {
    if (!isRecord(value)) {
        return null;
    }
    return {
        hasRoutePaths: booleanValue(value['has_route_paths']) ?? false,
        evidencePathCount: numberValue(value['evidence_path_count']),
        outlineCount: numberValue(value['outline_count']),
        citationCount: numberValue(value['citation_count']),
        sourceBackedCandidateCount: numberValue(
            value['source_backed_candidate_count'],
        ),
        indexValidationStatus: stringValue(value['index_validation_status']),
        mapWarningCount: numberValue(value['map_warning_count']),
        missingContextCount: numberValue(value['missing_context_count']),
        contextPageCount: numberValue(value['context_page_count']),
        runtimeAnchorCount: numberValue(value['runtime_anchor_count']),
        runtimeSourceReferenceCount: numberValue(
            value['runtime_source_reference_count'],
        ),
    };
}

function codebaseWikiLocalizationFromUnknown(
    localization: unknown,
): CodebaseWikiLocalization | null {
    if (!isRecord(localization)) {
        return null;
    }
    const summary = stringValue(localization['summary']);
    if (!summary) {
        return null;
    }
    return {
        summary,
        confidence: stringValue(localization['confidence']) ?? 'unknown',
        evidenceReferences: stringsFromUnknown(
            localization['evidence_references'],
        ),
        candidateLocations: codebaseWikiLocalizationCandidatesFromUnknown(
            localization['candidate_locations'],
        ),
        lowConfidenceCandidates: codebaseWikiLocalizationCandidatesFromUnknown(
            localization['low_confidence_candidates'],
        ),
        testReferences: codebaseWikiContextReferencesFromUnknown(
            localization['test_references'],
        ),
        searchTrail: codebaseWikiLocalizationTrailFromUnknown(
            localization['search_trail'],
        ),
        ruledOut: codebaseWikiLocalizationRuledOutFromUnknown(
            localization['ruled_out'],
        ),
        sourceBackedFindings: stringsFromUnknown(
            localization['source_backed_findings'],
        ),
        hypotheses: stringsFromUnknown(localization['hypotheses']),
    };
}

function getCodebaseWikiRuntimeHandoff(
    payload: AdminAgentArtifact['payload'],
): CodebaseWikiRuntimeEvidenceHandoff | null {
    const handoff = toRecord(payload)['runtime_handoff'];
    if (!isRecord(handoff)) {
        return null;
    }
    const localization = codebaseWikiLocalizationFromUnknown(
        handoff['localization'],
    );
    if (!localization) {
        return null;
    }
    return {
        issue: stringValue(handoff['issue']) ?? '',
        evidenceReferences: stringsFromUnknown(handoff['evidence_references']),
        lookups: codebaseWikiRuntimeLookupsFromUnknown(handoff['lookups']),
        anchors: codebaseWikiRuntimeEvidenceAnchorsFromUnknown(
            handoff['anchors'],
        ),
        unsupportedReferences: stringsFromUnknown(
            handoff['unsupported_references'],
        ),
        sourcePathCandidates: stringsFromUnknown(
            handoff['source_path_candidates'],
        ),
        localizationQuery: stringValue(handoff['localization_query']) ?? '',
        localization,
    };
}

function codebaseWikiRuntimeLookupsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiRuntimeEvidenceLookup> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((lookup) => {
        const reference = stringValue(lookup['reference']);
        if (!reference) {
            return [];
        }
        return [
            {
                reference,
                kind: stringValue(lookup['kind']) ?? 'unknown',
                status: stringValue(lookup['status']) ?? 'unknown',
                identifier: stringValue(lookup['identifier']),
                environment: stringValue(lookup['environment']),
                sourceTool: stringValue(lookup['source_tool']),
                extractedAnchorCount: numberValue(
                    lookup['extracted_anchor_count'],
                ),
                notes: stringsFromUnknown(lookup['notes']),
            },
        ];
    });
}

function codebaseWikiRuntimeEvidenceAnchorsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiRuntimeEvidenceAnchor> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((anchor) => {
        const valueText = stringValue(anchor['value']);
        if (!valueText) {
            return [];
        }
        return [
            {
                kind: stringValue(anchor['kind']) ?? 'unknown',
                label: stringValue(anchor['label']) ?? 'Runtime anchor',
                value: valueText,
                sourceReference: stringValue(anchor['source_reference']) ?? '',
                sourceBacked: anchor['source_backed'] === true,
                confidence: stringValue(anchor['confidence']) ?? 'unknown',
                citation: stringValue(anchor['citation']),
            },
        ];
    });
}

function codebaseWikiLocalizationCandidatesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiLocalizationCandidate> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((candidate) => {
        const path = stringValue(candidate['path']);
        if (!path) {
            return [];
        }
        return [
            {
                path,
                confidence: stringValue(candidate['confidence']) ?? 'unknown',
                score: numberValue(candidate['score']),
                reasons: stringsFromUnknown(candidate['reasons']),
                symbols: codebaseWikiLocalizationSymbolsFromUnknown(
                    candidate['symbols'],
                ),
                relationships: codebaseWikiLocalizationRelationshipsFromUnknown(
                    candidate['relationships'],
                ),
                sourceRanges: codebaseWikiSourceRangesFromUnknown(
                    candidate['source_ranges'],
                ),
                testReferences: codebaseWikiContextReferencesFromUnknown(
                    candidate['test_references'],
                ),
                sourceBackedFindings: stringsFromUnknown(
                    candidate['source_backed_findings'],
                ),
                hypotheses: stringsFromUnknown(candidate['hypotheses']),
            },
        ];
    });
}

function codebaseWikiLocalizationSymbolsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiLocalizationSymbol> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((symbol) => {
        const name = stringValue(symbol['name']);
        const kind = stringValue(symbol['kind']);
        const path = stringValue(symbol['path']);
        if (!name || !kind || !path) {
            return [];
        }
        return [
            {
                name,
                kind,
                path,
                reasons: stringsFromUnknown(symbol['reasons']),
            },
        ];
    });
}

function codebaseWikiLocalizationRelationshipsFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiLocalizationRelationship> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((relationship) => {
        const sourcePath = stringValue(relationship['source_path']);
        const importName = stringValue(relationship['import_name']);
        const kind =
            stringValue(relationship['kind']) ??
            stringValue(relationship['relationship']);
        if (!sourcePath || !importName || !kind) {
            return [];
        }
        return [
            {
                sourcePath,
                targetPath: stringValue(relationship['target_path']),
                importName,
                kind,
                relationship: stringValue(relationship['relationship']),
                reasons: stringsFromUnknown(relationship['reasons']),
            },
        ];
    });
}

function codebaseWikiSourceRangesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiSourceRange> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((sourceRange) => {
        const path = stringValue(sourceRange['path']);
        const lineStart = numberValue(sourceRange['line_start']);
        const lineEnd = numberValue(sourceRange['line_end']);
        if (!path || lineStart === null || lineEnd === null) {
            return [];
        }
        return [
            {
                path,
                lineStart,
                lineEnd,
                sourceUrl: safeCodebaseSourceUrl(sourceRange['source_url']),
                lines: codebaseWikiSourceLinesFromUnknown(sourceRange['lines']),
            },
        ];
    });
}

function codebaseWikiSourceLinesFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiSourceLine> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((line) => {
        const lineNumber = numberValue(line['line_number']);
        const text = stringValue(line['text']);
        if (lineNumber === null || text === null) {
            return [];
        }
        return [{ lineNumber, text }];
    });
}

function codebaseWikiLocalizationTrailFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiLocalizationTrailStep> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((step) => {
        const operation = stringValue(step['operation']);
        if (!operation) {
            return [];
        }
        return [
            {
                operation,
                query: stringValue(step['query']) ?? '',
                path: stringValue(step['path']),
                resultCount: numberValue(step['result_count']),
                inspectedPaths: stringsFromUnknown(step['inspected_paths']),
                notes: stringsFromUnknown(step['notes']),
            },
        ];
    });
}

function codebaseWikiLocalizationRuledOutFromUnknown(
    value: unknown,
): ReadonlyArray<CodebaseWikiLocalizationRuledOut> {
    if (typeof value === 'string' && value.trim()) {
        return [{ path: null, reason: value, evidence: [] }];
    }
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item) => {
        if (typeof item === 'string' && item.trim()) {
            return [{ path: null, reason: item, evidence: [] }];
        }
        if (!isRecord(item)) {
            return [];
        }
        const path = stringValue(item['path']);
        const reason = stringValue(item['reason']);
        if (!path && !reason) {
            return [];
        }
        return [
            {
                path,
                reason,
                evidence: stringsFromUnknown(item['evidence']),
            },
        ];
    });
}

function getCodebaseWikiMatches(
    payload: AdminAgentArtifact['payload'],
): ReadonlyArray<CodebaseWikiMatch> {
    const matches = toRecord(payload)['matches'];
    if (!Array.isArray(matches)) {
        return [];
    }
    return matches.filter(isRecord).flatMap((match) => {
        const path = stringValue(match['path']);
        if (!path) {
            return [];
        }
        return [
            {
                path,
                sourceUrl: safeCodebaseSourceUrl(match['source_url']),
                lineStart: numberValue(match['line_start']),
                lineEnd: numberValue(match['line_end']),
                language: stringValue(match['language']),
                score: numberValue(match['score']),
                reasons: stringsFromUnknown(match['reasons']),
                symbols: stringsFromUnknown(match['symbols']),
                imports: stringsFromUnknown(match['imports']),
                headings: stringsFromUnknown(match['headings']),
                snippet: stringValue(match['snippet']),
            },
        ];
    });
}

function getCodebaseWikiReferences(
    payload: AdminAgentArtifact['payload'],
): ReadonlyArray<CodebaseWikiReferenceMatch> {
    const references = toRecord(payload)['references'];
    if (!Array.isArray(references)) {
        return [];
    }
    return references.filter(isRecord).flatMap((reference) => {
        const path = stringValue(reference['path']);
        const symbolName = stringValue(reference['symbol_name']);
        const referenceKind = stringValue(reference['reference_kind']);
        if (!path || !symbolName || !referenceKind) {
            return [];
        }
        return [
            {
                path,
                sourceUrl: safeCodebaseSourceUrl(reference['source_url']),
                lineStart: numberValue(reference['line_start']),
                lineEnd: numberValue(reference['line_end']),
                language: stringValue(reference['language']),
                score: numberValue(reference['score']),
                reasons: stringsFromUnknown(reference['reasons']),
                referenceKind,
                symbolName,
                lineText: stringValue(reference['line_text']),
                readCommand: stringValue(reference['read_command']),
            },
        ];
    });
}

function getCodebaseWikiKnowledgeMatches(
    payload: AdminAgentArtifact['payload'],
): ReadonlyArray<CodebaseWikiKnowledgeMatch> {
    return codebaseWikiKnowledgeMatchesFromUnknown(
        toRecord(payload)['knowledge_matches'],
    );
}

function getCodebaseWikiOutlines(
    payload: AdminAgentArtifact['payload'],
): ReadonlyArray<CodebaseWikiOutline> {
    return codebaseWikiOutlinesFromUnknown(toRecord(payload)['outlines']);
}

function codebaseWikiOutlinesFromUnknown(
    outlines: unknown,
): ReadonlyArray<CodebaseWikiOutline> {
    if (!Array.isArray(outlines)) {
        return [];
    }
    return outlines.filter(isRecord).flatMap((outline) => {
        const path = stringValue(outline['path']);
        const language = stringValue(outline['language']);
        if (!path || !language) {
            return [];
        }
        return [
            {
                path,
                language,
                lineCount: numberValue(outline['line_count']),
                score: numberValue(outline['score']),
                reasons: stringsFromUnknown(outline['reasons']),
                roles: stringsFromUnknown(outline['roles']),
                imports: stringsFromUnknown(outline['imports']),
                headings: stringsFromUnknown(outline['headings']),
                symbols: codebaseWikiOutlineSymbolsFromUnknown(
                    outline['symbols'],
                ),
                sourceUrl: safeCodebaseSourceUrl(outline['source_url']),
            },
        ];
    });
}

function codebaseWikiOutlineSymbolsFromUnknown(
    symbols: unknown,
): ReadonlyArray<CodebaseWikiOutlineSymbol> {
    if (!Array.isArray(symbols)) {
        return [];
    }
    return symbols.filter(isRecord).flatMap((symbol) => {
        const name = stringValue(symbol['name']);
        const kind = stringValue(symbol['kind']);
        if (!name || !kind) {
            return [];
        }
        return [
            {
                name,
                kind,
                parentName: stringValue(symbol['parent_name']),
                signature: stringValue(symbol['signature']),
                scopeDepth: numberValue(symbol['scope_depth']),
                childCount: numberValue(symbol['child_count']),
                decorators: stringsFromUnknown(symbol['decorators']),
                tags: stringsFromUnknown(symbol['tags']),
                sourceUrl: safeCodebaseSourceUrl(symbol['source_url']),
                lineStart: numberValue(symbol['line_start']),
                lineEnd: numberValue(symbol['line_end']),
            },
        ];
    });
}

function codebaseWikiKnowledgeMatchesFromUnknown(
    matches: unknown,
): ReadonlyArray<CodebaseWikiKnowledgeMatch> {
    if (!Array.isArray(matches)) {
        return [];
    }
    return matches.filter(isRecord).flatMap((match) => {
        const path = stringValue(match['path']);
        const title = stringValue(match['title']);
        const documentType = stringValue(match['document_type']);
        if (!path || !title || !documentType) {
            return [];
        }
        return [
            {
                path,
                documentType,
                title,
                sourceUrl: safeCodebaseSourceUrl(match['source_url']),
                lineStart: numberValue(match['line_start']),
                lineEnd: numberValue(match['line_end']),
                score: numberValue(match['score']),
                reasons: stringsFromUnknown(match['reasons']),
                snippet: stringValue(match['snippet']),
            },
        ];
    });
}
